import { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { Stage, Layer, Image as KonvaImage, Rect, Transformer, Shape, Text, Group, Circle as KonvaCircle, Line as KonvaLine, Path as KonvaPath } from "react-konva";
import useImage from "use-image";
import Konva from "konva";
import { X, Loader2, Save, Copy, Layers, Brush, Eraser, Trash2, Sun, Eye, Sparkles, MousePointerClick, Square, Circle, Heart, Plus, Check, ChevronDown, RotateCcw, RotateCw, Minus, ZoomIn, PenTool, Undo2 } from "lucide-react";
import { apiClient } from "../../lib/api";
import type { CustomMockup, MockupAnalysis, MockupColorLabel } from "@erc/shared";
import { normalizeColorLabel, colorLabelText, colorLabelBox, colorLabelBrushPath, DEFAULT_COLOR_LABEL, MOCKUP_DETECT_MODELS, DEFAULT_MOCKUP_DETECT_MODEL } from "@erc/shared";
import { authHeaders } from "../../lib/http";
import type { useSupplierVariations } from "../../lib/useSupplierVariations";
import { useAnalysisDraft, MockupAnalysisFields, themeForItem } from "./MockupAnalysisModal";
import env from "../../env";
import { SHAPE_OPTIONS, isShaped, placeInArea, traceAreaShape, hasWarp, renderWarpedArea, type AreaShape, type AreaFit, type AreaWarp } from "../../lib/area-shape";
import { maskToAlpha, surfaceCover, SURFACE_MIN_COVER } from "../../lib/mockup-finish-canvas";
import { warpMapOf, areaCorners, perspectiveQuad, cylinderParams } from "@erc/shared";
import type { SamSession } from "../../lib/sam-segment";
import { isStaleChunkError, reloadForStaleChunk } from "../../lib/staleChunk";

const API_BASE = env.API_URL;

// Son kaydedilen renk etiketi bicimi (yazi haric): yeni mockup'ta etiket acilinca varsayilan olur
const LAST_LABEL_KEY = "erc:mockup:last-color-label";
function lastColorLabel(): MockupColorLabel {
  try {
    const v = normalizeColorLabel(JSON.parse(localStorage.getItem(LAST_LABEL_KEY) || "null"));
    if (v) return { ...v, text: null };
  } catch { /* yok */ }
  return DEFAULT_COLOR_LABEL;
}
function rememberColorLabel(l: MockupColorLabel) {
  try { localStorage.setItem(LAST_LABEL_KEY, JSON.stringify({ ...l, text: null })); } catch { /* yok */ }
}

// ======== Design Area Editor (shared by Mockup Yonetimi + Mockup Studio) ========

export const AREA_COLORS = [
  { fill: "rgba(99,102,241,0.1)", stroke: "#6366f1", label: "indigo" },
  { fill: "rgba(16,185,129,0.1)", stroke: "#10b981", label: "green" },
  { fill: "rgba(245,158,11,0.1)", stroke: "#f59e0b", label: "amber" },
  { fill: "rgba(244,63,94,0.1)", stroke: "#f43f5e", label: "rose" },
  { fill: "rgba(14,165,233,0.1)", stroke: "#0ea5e9", label: "sky" },
  { fill: "rgba(139,92,246,0.1)", stroke: "#8b5cf6", label: "violet" },
];

const AREA_LABELS = [
  { id: "on", label: "Ön" },
  { id: "arka", label: "Arka" },
  { id: "sol-kol", label: "Sol Kol" },
  { id: "sag-kol", label: "Sağ Kol" },
  { id: "yaka", label: "Yaka / Etiket" },
  { id: "cep", label: "Cep" },
];

// Max design areas per mockup. Generous cap to support bulk/all-over
// layouts; named labels above cover common apparel spots, extra areas get
// auto-generated "Alan N" labels and cycle through the color palette.
export const MAX_AREAS = 12;

// Display text for an area label id (named spots, or auto "alan-N" -> "Alan N").
export function areaLabelText(id: string): string {
  const found = AREA_LABELS.find(l => l.id === id);
  if (found) return found.label;
  const m = /^alan-(\d+)$/.exec(id);
  return m ? `Alan ${m[1]}` : id;
}

export interface AreaRect {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  label: string;
  theme?: "light" | "dark"; // light -> white label, dark -> black label
  shape?: AreaShape;        // baski yuzeyinin sekli (tasarim buna kirpilir)
  fit?: AreaFit;            // contain | fill (sekilli alanda varsayilan fill)
  warp?: AreaWarp | null;   // silindir (kupa) / perspektif (paspas, tabak)
  surfaceUrl?: string;      // kendi urun yuzeyi: tasarim yalnizca orada (ust uste urun / katli kol)
}

/** Tasarim katmanini (tam tuval) alanin yuzeyine kirp: alfa tuval, opak = gorunur */
function clipCanvasToSurface(layer: HTMLCanvasElement, surface: CanvasImageSource) {
  const ctx = layer.getContext("2d")!;
  ctx.save();
  ctx.globalCompositeOperation = "destination-in";
  ctx.drawImage(surface, 0, 0, layer.width, layer.height);
  ctx.restore();
}


/** Dondurulmus alanin merkezi (gorsel koordinati) */
function areaCenter(a: AreaRect) {
  const rad = ((a.rotation || 0) * Math.PI) / 180;
  const c = Math.cos(rad), s = Math.sin(rad);
  return { x: a.x + (a.w / 2) * c - (a.h / 2) * s, y: a.y + (a.w / 2) * s + (a.h / 2) * c };
}

/** Serbest oranli alan: sekilli (egik daire = elips) ya da carpitilmis (kupa
 *  sarmasi, paspas). Duz dikdortgen alan eskisi gibi kare kalir (tisort). */
const isFreeAspect = (a: AreaRect) => isShaped(a) || !!a.warp;
/** Dikdortgen alan = baski alani orani (tisort 4200x4800 -> genislik:yukseklik 7:8) */
export const PRINT_RATIO = 7 / 8;
const isPrintRatio = (a: AreaRect) => Math.abs(a.w / Math.max(1, a.h) - PRINT_RATIO) < 0.02;
/** Alani merkezini ve yuksekligini koruyarak 7:8'e cevirir (donmus alan dahil) */
function toPrintRatio(a: AreaRect): AreaRect {
  const nw = a.h * PRINT_RATIO;
  const rad = ((a.rotation || 0) * Math.PI) / 180, c = Math.cos(rad), s = Math.sin(rad);
  const cx = a.x + c * (a.w / 2) - s * (a.h / 2), cy = a.y + s * (a.w / 2) + c * (a.h / 2);
  return { ...a, w: Math.round(nw), x: Math.round(cx - c * (nw / 2) + s * (a.h / 2)), y: Math.round(cy - s * (nw / 2) - c * (a.h / 2)) };
}

/** Seklin kendi kutusunu ne kadar doldurdugu (SAM aday secimi icin) */
const SHAPE_FILL: Record<AreaShape, number> = { rect: 1, ellipse: 0.785, heart: 0.7 };

// Fit a design into an area's local box (centered, aspect-preserved).
function fitLocal(w: number, h: number, natW: number, natH: number) {
  if (!natW || !natH) return { x: 0, y: 0, width: w, height: h };
  const ar = w / h, dr = natW / natH;
  let dw: number, dh: number;
  if (dr > ar) { dw = w; dh = w / dr; } else { dh = h; dw = h * dr; }
  // Dik/kareye yakin alanda ust hizali (tisort baskisi yakanin altindan; sunucu ve Studyo ile ayni)
  return { x: (w - dw) / 2, y: h >= w * 0.95 ? 0 : (h - dh) / 2, width: dw, height: dh };
}

/** Tasarimi alanina tuval baglaminda ciz (onizleme/bitis ile ayni geometri) */
function drawAreaDesign(ctx: CanvasRenderingContext2D, img: HTMLImageElement, a: AreaRect) {
  if (hasWarp(a)) { const r = renderWarpedArea(img, a); ctx.drawImage(r.canvas, r.left, r.top); return; }
  const f = a.fit || isShaped(a) ? placeInArea(a, img.naturalWidth, img.naturalHeight) : fitLocal(a.w, a.h, img.naturalWidth, img.naturalHeight);
  ctx.save();
  ctx.translate(a.x, a.y); ctx.rotate(((a.rotation || 0) * Math.PI) / 180);
  if (isShaped(a)) { traceAreaShape(ctx, a.shape, a.w, a.h); ctx.clip(); }
  ctx.drawImage(img, f.x, f.y, f.width, f.height);
  ctx.restore();
}

// Renders the test/sample design inside a design area (editor preview).
function AreaTestImage({ url, area, opacity, surface, surfVersion, imageW, imageH }: { url: string; area: AreaRect; opacity: number; surface?: CanvasImageSource; surfVersion?: number; imageW: number; imageH: number }) {
  const [img] = useImage(url, "anonymous");
  // Kendi yuzeyine kirpilan alan: tam tuvalde ciz, yuzeyle kes
  const clipped = useMemo(() => {
    if (!img || !surface) return null;
    const c = document.createElement("canvas");
    c.width = imageW; c.height = imageH;
    drawAreaDesign(c.getContext("2d")!, img, area);
    clipCanvasToSurface(c, surface);
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, surface, imageW, imageH, surfVersion, JSON.stringify(area)]);
  const warped = useMemo(
    () => (img && hasWarp(area) ? renderWarpedArea(img, area) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [img, area.x, area.y, area.w, area.h, area.rotation, area.shape, area.fit, JSON.stringify(area.warp ?? null)],
  );
  if (!img) return null;
  if (clipped) return <KonvaImage image={clipped} x={0} y={0} width={imageW} height={imageH} opacity={opacity} listening={false} />;
  if (warped) return <KonvaImage image={warped.canvas} x={warped.left} y={warped.top} opacity={opacity} listening={false} />;
  const f = area.fit || isShaped(area)
    ? placeInArea(area, img.naturalWidth, img.naturalHeight)
    : fitLocal(area.w, area.h, img.naturalWidth, img.naturalHeight);
  return (
    <Group x={area.x} y={area.y} rotation={area.rotation || 0} listening={false}
      clipFunc={isShaped(area) ? (ctx) => traceAreaShape(ctx._context as CanvasRenderingContext2D, area.shape, area.w, area.h) : undefined}>
      <KonvaImage image={img} x={f.x} y={f.y} width={f.width} height={f.height} opacity={opacity} listening={false} />
    </Group>
  );
}

export function DesignAreaEditor({
  mockup,
  onSave,
  onSaveAndApplyAll,
  onCancel,
  seedAreas,
  backLabel,
  testDesignLight,
  testDesignDark,
  apparel,
}: {
  /** Giyim mockupu: analiz formu panelde, otomatik kurulum analizi de yapar, Kaydet ile birlikte kaydedilir */
  apparel?: { vars?: ReturnType<typeof useSupplierVariations>; brandOptions?: string[]; onSaved: (m: CustomMockup) => void };
  mockup: CustomMockup;
  onSave: (areas: AreaRect[]) => Promise<void> | void;
  onSaveAndApplyAll?: (areas: AreaRect[]) => Promise<void> | void;
  onCancel: () => void;
  seedAreas?: AreaRect[]; // pre-fill (e.g. the areas being bulk-applied)
  backLabel?: string;     // label for the cancel/back button
  testDesignLight?: string | null;
  testDesignDark?: string | null;
}) {
  const imgUrl = mockup.imageUrl.startsWith("http") ? mockup.imageUrl : `${API_BASE}${mockup.imageUrl}`;
  const [image] = useImage(imgUrl, "anonymous");

  // Load existing areas from designAreas / legacy / a seed (apply-modal edit)
  const initialAreas: AreaRect[] = (() => {
    const da = (mockup as any).designAreas;
    if (Array.isArray(da) && da.length > 0) return da;
    if (mockup.designAreaW > 20 && mockup.designAreaH > 20) {
      return [{ id: "area-1", x: mockup.designAreaX, y: mockup.designAreaY, w: mockup.designAreaW, h: mockup.designAreaH, rotation: mockup.designAreaRotation || 0, label: "on" }];
    }
    // Baska mockuptan gelen alan: yuzey maskesi o fotografa ait, tasinmaz
    if (seedAreas && seedAreas.length > 0) return seedAreas.map(({ surfaceUrl: _s, ...a }) => ({ ...a, id: `seed-${a.id}` }));
    return [];
  })();

  const [areas, setAreas] = useState<AreaRect[]>(initialAreas);
  // Renk tablosu (onlarca urun tek gorselde): alan siniri yok sayilacak kadar yuksek
  const isChart = (mockup.kind as string | undefined) === "chart";
  const maxAreas = isChart ? 60 : MAX_AREAS;
  // "AI ile kur" modeli: son secim hatirlanir
  const [detectModel, setDetectModel] = useState<string>(() => {
    try { const v = localStorage.getItem("erc:mockup:detect-model"); return MOCKUP_DETECT_MODELS.some((m) => m.id === v) ? v! : DEFAULT_MOCKUP_DETECT_MODEL; } catch { return DEFAULT_MOCKUP_DETECT_MODEL; }
  });
  const pickDetectModel = (v: string) => { setDetectModel(v); try { localStorage.setItem("erc:mockup:detect-model", v); } catch { /* yok */ } };
  /** Tum alanlar 1. alanin boyutuna (merkezleri yerinde kalir) */
  const equalizeToFirst = () => setAreas((prev) => {
    if (prev.length < 2) return prev;
    const { w, h } = prev[0];
    return prev.map((a, i) => (i === 0 ? a : { ...a, x: Math.round(a.x + a.w / 2 - w / 2), y: Math.round(a.y + a.h / 2 - h / 2), w, h }));
  });
  // Giyim mockup analizi: form burada; otomatik kurulum ayni AI cagrisiyla doldurur
  const draft = useAnalysisDraft(mockup, apparel?.vars, apparel?.brandOptions);
  const [analyzing, setAnalyzing] = useState(false);
  /** Yalniz analiz (alanlar zaten kuruluysa) — otomatik kurulum bunu zaten yapar */
  async function runAnalysis() {
    setAnalyzing(true);
    try {
      const r = await fetch(`${API_BASE}/api/mockups/${mockup.id}/analyze`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ force: true }) });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.data) draft.fill(j.data as MockupAnalysis);
    } finally { setAnalyzing(false); }
  }
  // Alan temasi = tasarim rengi, urun tonundan: urun sayisi alan sayisina esitse sirayla esitlenir
  const toneKey = draft.items.map((i) => `${i.tone}:${i.color}`).join(",") + `|${draft.brand} ${draft.model}`;
  useEffect(() => {
    if (!apparel || !draft.items.length) return;
    setAreas((cur) => {
      if (cur.length !== draft.items.length) return cur;
      let changed = false;
      const next = cur.map((a, k) => {
        const t = themeForItem(draft.items[k], `${draft.brand} ${draft.model}`);
        if ((a.theme ?? "light") === t) return a;
        changed = true;
        return { ...a, theme: t };
      });
      return changed ? next : cur;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toneKey]);
  const [selectedIdx, setSelectedIdx] = useState<number>(areas.length > 0 ? 0 : -1);
  const [drawing, setDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [drawingRect, setDrawingRect] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const rectRefs = useRef<(Konva.Shape | null)[]>([]);
  const transformerRef = useRef<Konva.Transformer>(null);
  const depthDoneRef = useRef(!!mockup.depthUrl);
  // Alan yuzeyleri (alfa tuval, id -> tuval). Yuklenmemis olanlar kayitta yuklenir.
  const [surfaces, setSurfaces] = useState<Record<string, HTMLCanvasElement>>({});
  const pendingSurfacesRef = useRef<Map<string, HTMLCanvasElement>>(new Map());
  // Alan yuzeyi duzenleme: maske araclari bu alanin yuzeyine uygulanir;
  // mor = bu alanin tasarimi gorunmez
  const [surfaceEditId, setSurfaceEditId] = useState<string | null>(null);
  const [surfVersion, setSurfVersion] = useState(0);
  const surfDispRef = useRef<HTMLCanvasElement | null>(null);
  const surfImgRef = useRef<Konva.Image | null>(null);
  const maskSectionRef = useRef<HTMLElement | null>(null);
  const hoveredIdxRef = useRef<number>(-1); // area under the mouse (for A/D rotate)
  const [saving, setSaving] = useState(false);
  // ON PLAN (occlusion) — ayri bir ekran yerine ALANLARIN UZERINDE boyanir.
  // Boyanan yerlerde tasarim silinir, mockup'in kendi pikseli (kumas kivrimi,
  // parlama, sac) on planda kalir. Firca yalnizca tanimli alanlarin icinde
  // is gorur; alanin kendisi (konum/boyut) bundan etkilenmez.
  const [maskMode, setMaskMode] = useState(false);
  const [maskErase, setMaskErase] = useState(false);
  // Maske araci: firca ya da CokGEN (noktalarla alan sec, kapaninca doldurulur)
  const [maskTool, setMaskTool] = useState<"brush" | "polygon">("brush");
  const [polyPts, setPolyPts] = useState<Array<{ x: number; y: number }>>([]);
  const [polyHover, setPolyHover] = useState<{ x: number; y: number } | null>(null);
  const [brushSize, setBrushSize] = useState(Math.max(20, Math.round(mockup.imageWidth / 25)));
  // Alanin EN PARLAK yuzde kaci on plana alinsin (mutlak esik degil)
  const [brightPercent, setBrightPercent] = useState(12);
  const [maskDirty, setMaskDirty] = useState(false);
  const [preview, setPreview] = useState(false);   // kirmizi yerine gercek sonuc
  const [maskVersion, setMaskVersion] = useState(0);   // Konva overlay tazeleme
  const [hasMask, setHasMask] = useState(Boolean(mockup.maskUrl));
  // Alanin yuzde kaci on plana alindi — %85 ustu tasarimin tamamen kaybolmasi demek
  const [maskCoverage, setMaskCoverage] = useState(0);
  const maskRef = useRef<HTMLCanvasElement | null>(null);   // siyah zemin + beyaz isaret (kaydedilen)
  const dispRef = useRef<HTMLCanvasElement | null>(null);   // ekranda kirmizi gosterim
  const paintingRef = useRef(false);
  const maskImgRef = useRef<Konva.Image | null>(null);   // katmani yeniden cizmek icin
  const lastPtRef = useRef<{ x: number; y: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  // YUZEY MASKESI: maske tum gorseli kapsar, yalnizca urunun baski yuzeyi
  // (AI ile bulunan) siyah kalir -> delik, ip, kenar ve arka plan tasarimin
  // onunde durur. Klasik on plan boyasindan farki: firca alanla sinirli degil.
  const [surfaceMode, setSurfaceMode] = useState(false);
  const surfaceModeRef = useRef(false);
  surfaceModeRef.current = surfaceMode;
  const [aiClick, setAiClick] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiStatus, setAiStatus] = useState("");
  const samRef = useRef<Promise<SamSession> | null>(null);
  // ISIK: urunun parlama/golgesi tasarima geri bindirilir (0 = kapali)
  const [shading, setShading] = useState<number>(mockup.shading ?? 0);
  // KIRISIK: tasarim kumas kivrimi boyunca egilir (0 = kapali)
  const [displace, setDisplace] = useState<number>(mockup.displace ?? 0);
  // RENK ETIKETI: renk adi mockup uzerine basilir (kalici ayar, sunucu render'i da basar)
  const [colorName, setColorName] = useState(mockup.color ?? "");
  const [colorLabel, setColorLabel] = useState<MockupColorLabel | null>(normalizeColorLabel(mockup.colorLabel));
  const [labelSelected, setLabelSelected] = useState(false);
  // Damlalik: siradaki tiklamada tisortun rengi etiket zeminine alinir
  const [pickLabelBg, setPickLabelBg] = useState(false);
  const labelRef = useRef<Konva.Group>(null);
  const labelTrRef = useRef<Konva.Transformer>(null);
  const [fontReady, setFontReady] = useState(false);
  useEffect(() => {
    // Sunucu Montserrat 600 ile basar; onizleme ayni fontla olculsun
    document.fonts?.load("600 48px Montserrat").then(() => setFontReady(true)).catch(() => setFontReady(true));
  }, []);
  // Etiketi olmayan mockup'ta etiket acilinca son kaydedilen bicim gelir (buyuk harf, sekil, boyut, konum, renk)
  const patchLabel = (p: Partial<MockupColorLabel>) => setColorLabel((c) => ({ ...(c ?? lastColorLabel()), ...p }));
  const labelText = colorLabel?.enabled ? colorLabelText(colorLabel, colorName) : "";
  const labelBox = useMemo(() => {
    if (!colorLabel || !labelText) return null;
    const ctx = document.createElement("canvas").getContext("2d")!;
    return colorLabelBox(colorLabel, mockup.imageWidth, mockup.imageHeight, (fs) => {
      ctx.font = `600 ${fs}px Montserrat`;
      return ctx.measureText(labelText).width;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorLabel, labelText, fontReady, mockup.imageWidth, mockup.imageHeight]);
  useEffect(() => { if (selectedIdx >= 0) setLabelSelected(false); }, [selectedIdx]);
  useEffect(() => {
    const tr = labelTrRef.current;
    if (!tr) return;
    tr.nodes(labelSelected && labelRef.current ? [labelRef.current] : []);
    tr.getLayer()?.batchDraw();
  }, [labelSelected, labelBox]);

  // Sag panel (~360px) + kenar bosluklari dusulur
  const maxW = Math.min(mockup.imageWidth, Math.max(320, window.innerWidth - 460));
  const maxH = Math.min(mockup.imageHeight, Math.max(320, window.innerHeight * 0.95 - 150));
  const scaleX = maxW / mockup.imageWidth;
  const scaleY = maxH / mockup.imageHeight;
  const fitScale = Math.min(1, scaleX, scaleY);
  const scale = fitScale * zoom; // effective scale (fit-to-view * zoom)
  const canvasW = mockup.imageWidth * scale;

  // ---- YAKINLASTIRMA: imlece dogru (Ctrl+tekerlek), alana odaklan (F),
  // bosluk+surukle ile kaydir. Kaydirma tuvali saran kutunun scroll'u ile.
  const MAX_ZOOM = 8;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Yeni olcek uygulaninca hangi gorsel noktasi ekranin neresine gelsin
  const anchorRef = useRef<{ ix: number; iy: number; vx: number; vy: number } | null>(null);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const panRef = useRef<{ x: number; y: number; sl: number; st: number } | null>(null);
  const PAD = 16; // kaydirma kutusunun ic boslugu (p-4)

  /** Olcegi degistir; (vx,vy) ekran noktasindaki gorsel pikseli yerinde kalsin */
  function zoomTo(nextZoom: number, vx?: number, vy?: number) {
    const el = scrollRef.current;
    const z = Math.max(0.25, Math.min(MAX_ZOOM, nextZoom));
    if (el) {
      const px = vx ?? el.clientWidth / 2, py = vy ?? el.clientHeight / 2;
      anchorRef.current = {
        ix: (el.scrollLeft + px - PAD) / scale,
        iy: (el.scrollTop + py - PAD) / scale,
        vx: px, vy: py,
      };
    }
    setZoom(+z.toFixed(3));
  }

  /** Secili alana (yoksa tum alanlara) yakinlas ve ortala */
  function focusAreas() {
    const el = scrollRef.current;
    const list = selectedIdx >= 0 && areas[selectedIdx] ? [areas[selectedIdx]] : areas;
    if (!el || list.length === 0) return;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const a of list) {
      const rad = ((a.rotation || 0) * Math.PI) / 180, c = Math.cos(rad), sn = Math.sin(rad);
      for (const [lx, ly] of [[0, 0], [a.w, 0], [a.w, a.h], [0, a.h]]) {
        const gx = a.x + lx * c - ly * sn, gy = a.y + lx * sn + ly * c;
        x0 = Math.min(x0, gx); y0 = Math.min(y0, gy); x1 = Math.max(x1, gx); y1 = Math.max(y1, gy);
      }
    }
    const bw = (x1 - x0) * 1.15, bh = (y1 - y0) * 1.15;
    const vw = el.clientWidth - PAD * 2, vh = el.clientHeight - PAD * 2;
    const target = Math.min(vw / bw, vh / bh);
    const z = Math.max(0.25, Math.min(MAX_ZOOM, target / fitScale));
    anchorRef.current = { ix: (x0 + x1) / 2, iy: (y0 + y1) / 2, vx: el.clientWidth / 2, vy: el.clientHeight / 2 };
    setZoom(+z.toFixed(3));
  }

  // Olcek degisince capa noktasini yerine oturt
  useLayoutEffect(() => {
    const el = scrollRef.current, a = anchorRef.current;
    if (!el || !a) return;
    anchorRef.current = null;
    el.scrollLeft = a.ix * scale + PAD - a.vx;
    el.scrollTop = a.iy * scale + PAD - a.vy;
  }, [scale]);

  // Ctrl/Cmd + tekerlek: imlece dogru yakinlas (pasif olmayan dinleyici sart)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const factor = Math.exp(-e.deltaY * 0.0025);
      zoomTo(zoom * factor, e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, scale]);

  // Bosluk tusu: basili tutarken surukleme = kaydirma
  useEffect(() => {
    const isTyping = (t: EventTarget | null) => !!t && /^(INPUT|SELECT|TEXTAREA)$/.test((t as HTMLElement).tagName);
    const down = (e: KeyboardEvent) => {
      if (e.code === "Space" && !isTyping(e.target)) { e.preventDefault(); setSpaceHeld(true); }
      if ((e.key === "f" || e.key === "F") && !isTyping(e.target) && !e.ctrlKey && !e.metaKey) focusAreas();
      if ((e.key === "p" || e.key === "P") && !isTyping(e.target) && !e.ctrlKey && !e.metaKey) setPreview((v) => !v);
    };
    const up = (e: KeyboardEvent) => { if (e.code === "Space") { setSpaceHeld(false); panRef.current = null; } };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas, selectedIdx, scale]);
  // Sekilli alanda yuzey seklin cogunu kapsamiyorsa bozuk sayilir (Studio/sunucu ile ayni)
  const usableSurface = (a: AreaRect): HTMLCanvasElement | null => {
    const surf = surfaces[a.id];
    if (!surf) return null;
    return isShaped(a) && surfaceCover(surf, { x: a.x, y: a.y, width: a.w, height: a.h, rotation: a.rotation }) < SURFACE_MIN_COVER ? null : surf;
  };
  // Eski (onarilmamis) yuzey maskeleri acilista bir kez sunucuda onarilir: dikdortgen
  // delik / urunun kendisini disarida birakan maske tasarimi siliyordu
  const healTried = useRef(false);
  useEffect(() => {
    if (healTried.current || !areas.some((a) => a.surfaceUrl && !a.surfaceUrl.includes("surfh-"))) return;
    healTried.current = true;
    apiClient.healMockupSurfaces(mockup.id).then((r) => {
      if (!r.healed && !r.designAreas.length) return;
      const byId = new Map((r.designAreas as Array<{ id: string; surfaceUrl?: string }>).map((x) => [x.id, x.surfaceUrl]));
      setSurfaces({});
      setAreas((prev) => prev.map((a) => (byId.get(a.id) ? { ...a, surfaceUrl: byId.get(a.id) } : a)));
    }).catch(() => { /* onarilamazsa eski maske kalir */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas]);

  // Kayitli yuzey maskelerini yukle (alfa tuvale cevrilir)
  useEffect(() => {
    for (const a of areas) {
      if (!a.surfaceUrl || surfaces[a.id]) continue;
      const u = a.surfaceUrl.startsWith("http") ? a.surfaceUrl : `${API_BASE}${a.surfaceUrl}`;
      const im = new window.Image();
      im.crossOrigin = "anonymous";
      im.onload = () => setSurfaces((prev) => ({ ...prev, [a.id]: maskToAlpha(im, mockup.imageWidth, mockup.imageHeight) }));
      im.src = u;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas]);

  /** Kaydetmeden once: AI'nin cikardigi yuzeyleri yukle, alanlara url yaz */
  async function areasWithUploadedSurfaces(list: AreaRect[]): Promise<AreaRect[]> {
    const out: AreaRect[] = [];
    for (const a of list) {
      const c = pendingSurfacesRef.current.get(a.id);
      if (!c) { out.push(a); continue; }
      // Alfa tuval -> beyaz/siyah PNG (sunucu griyi alfa sayar)
      const bw = document.createElement("canvas");
      bw.width = c.width; bw.height = c.height;
      const bctx = bw.getContext("2d")!;
      bctx.fillStyle = "#000"; bctx.fillRect(0, 0, bw.width, bw.height);
      bctx.drawImage(c, 0, 0);
      const { url } = await apiClient.uploadAreaSurface(mockup.id, bw.toDataURL("image/png"), { x: a.x, y: a.y, w: a.w, h: a.h });
      pendingSurfacesRef.current.delete(a.id);
      out.push({ ...a, surfaceUrl: url });
    }
    return out;
  }

  /** Duzenlenen yuzeyin mor gosterimi: yuzey olmayan her yer mor */
  function rebuildSurfaceDisplay(id = surfaceEditId) {
    const surf = id ? surfaces[id] : null;
    if (!surf) { surfDispRef.current = null; return; }
    const c = surfDispRef.current && surfDispRef.current.width === surf.width ? surfDispRef.current : document.createElement("canvas");
    c.width = surf.width; c.height = surf.height;
    const ctx = c.getContext("2d")!;
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "rgba(139,92,246,0.55)"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.globalCompositeOperation = "destination-out";
    ctx.drawImage(surf, 0, 0);
    ctx.globalCompositeOperation = "source-over";
    surfDispRef.current = c;
  }

  function startSurfaceEdit(idx: number) {
    const a = areas[idx];
    if (!a || !surfaces[a.id]) return;
    setSelectedIdx(idx);
    setSurfaceEditId(a.id);
    setMaskMode(true);
    setAiClick(false);
    rebuildSurfaceDisplay(a.id);
    setSurfVersion((v) => v + 1);
    setTimeout(() => maskSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }

  /** Yuzeye bir sekil uygula: gizle = yuzeyden cikar, goster = ekle */
  function applyToSurface(draw: (ctx: CanvasRenderingContext2D) => void, hide: boolean) {
    const id = surfaceEditId;
    const surf = id ? surfaces[id] : null;
    if (!id || !surf) return;
    const ctx = surf.getContext("2d")!;
    ctx.save();
    ctx.globalCompositeOperation = hide ? "destination-out" : "source-over";
    ctx.fillStyle = "#fff"; ctx.strokeStyle = "#fff";
    draw(ctx);
    ctx.restore();
    const d = surfDispRef.current;
    if (d) {
      const dctx = d.getContext("2d")!;
      dctx.save();
      dctx.globalCompositeOperation = hide ? "source-over" : "destination-out";
      dctx.fillStyle = "rgba(139,92,246,0.55)"; dctx.strokeStyle = dctx.fillStyle;
      draw(dctx);
      dctx.restore();
      // Ust uste firca darbeleri moru koyulastirmasin: darbe sonunda yeniden kurulur
    }
    pendingSurfacesRef.current.set(id, surf);
    surfImgRef.current?.getLayer()?.batchDraw();
  }

  // ONIZLEMEDE ISIK + KIRISIK: Studio ile ayni bitis (ortak finishDesignCanvas).
  // Haritalar sunucuda o anki (kaydedilmemis) alan/maske ile uretilir; surukleme
  // bitince ~0.5 sn sonra guncellenir, arada duz tasarim gorunur.
  const [finished, setFinished] = useState<{ key: string; canvas: HTMLCanvasElement } | null>(null);
  const [finishing, setFinishing] = useState(false);
  const finishRunRef = useRef(0);
  const finishKey = JSON.stringify({ areas, shading, displace, maskVersion, testDesignLight, testDesignDark, surf: Object.keys(surfaces), surfVersion });
  const wantsFinish = preview && !maskMode && !!image && areas.length > 0 && (shading > 0 || displace > 0) && !!(testDesignLight || testDesignDark);
  useEffect(() => {
    if (!wantsFinish || finished?.key === finishKey) return;
    const run = ++finishRunRef.current;
    const t = setTimeout(async () => {
      setFinishing(true);
      try {
        if (displace > 0 && !depthDoneRef.current && image) {
          const { estimateDepth } = await import("../../lib/depth-estimate");
          await apiClient.saveMockupDepth(mockup.id, await estimateDepth(image, setAiStatus)).catch(() => {});
          depthDoneRef.current = true;
          setAiStatus("");
        }
        const maps = await apiClient.mockupPreviewMaps(mockup.id, {
          areas, shading, displace, mask: maskDirty && maskRef.current ? maskRef.current.toDataURL("image/png") : undefined,
        });
        const load = (u: string) => new Promise<HTMLImageElement | null>((res) => {
          const im = new window.Image(); im.crossOrigin = "anonymous";
          im.onload = () => res(im); im.onerror = () => res(null); im.src = u;
        });
        const W = mockup.imageWidth, H = mockup.imageHeight;
        const cv = document.createElement("canvas");
        cv.width = W; cv.height = H;
        const ctx = cv.getContext("2d")!;
        ctx.globalAlpha = mockup.opacity / 100;
        for (const a of areas) {
          const u = (a.theme ?? "light") === "dark" ? (testDesignDark ?? testDesignLight) : (testDesignLight ?? testDesignDark);
          const img = u ? await load(u) : null;
          if (!img) continue;
          const surf = usableSurface(a);
          if (!surf) { drawAreaDesign(ctx, img, a); continue; }
          const tmp = document.createElement("canvas");
          tmp.width = W; tmp.height = H;
          drawAreaDesign(tmp.getContext("2d")!, img, a);
          clipCanvasToSurface(tmp, surf);
          ctx.drawImage(tmp, 0, 0);
        }
        const [shadeImg, dispImg] = await Promise.all([maps.shade ? load(maps.shade) : null, maps.disp ? load(maps.disp) : null]);
        const { finishDesignCanvas } = await import("../../lib/mockup-finish-canvas");
        finishDesignCanvas(cv, { shadeImg, dispImg, displace, noise: maps.noise });
        if (run === finishRunRef.current) setFinished({ key: finishKey, canvas: cv });
      } catch (err) {
        setAiStatus("Önizleme ışığı üretilemedi: " + (err instanceof Error ? err.message : String(err)));
      } finally {
        if (run === finishRunRef.current) setFinishing(false);
      }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsFinish, finishKey]);
  const showFinished = wantsFinish && finished?.key === finishKey;

  const canvasH = mockup.imageHeight * scale;

  useEffect(() => {
    if (transformerRef.current && selectedIdx >= 0 && rectRefs.current[selectedIdx] && !drawing && !maskMode) {
      transformerRef.current.nodes([rectRefs.current[selectedIdx]!]);
      transformerRef.current.getLayer()?.batchDraw();
    } else if (transformerRef.current) {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedIdx, areas, drawing, maskMode]);

  // --- Maske tuvalleri: mockup cozunurlugunde, bir kez kurulur
  useEffect(() => {
    const W = mockup.imageWidth, H = mockup.imageHeight;
    const m = document.createElement("canvas"); m.width = W; m.height = H;
    const mctx = m.getContext("2d")!;
    mctx.fillStyle = "#000"; mctx.fillRect(0, 0, W, H);
    maskRef.current = m;

    const d = document.createElement("canvas"); d.width = W; d.height = H;
    dispRef.current = d;

    if (!mockup.maskUrl) { setMaskVersion((v) => v + 1); return; }
    // Kayitli maskeyi yukle — daha once boyanmis yerler kaybolmasin
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      mctx.drawImage(img, 0, 0, W, H);
      // Kose pikseli beyazsa maske tum gorseli kapsiyor = yuzey maskesi
      if (mctx.getImageData(1, 1, 1, 1).data[0] > 200) { surfaceModeRef.current = true; setSurfaceMode(true); }
      rebuildDisplayFromMask();
      setMaskVersion((v) => v + 1);
    };
    img.onerror = () => setMaskVersion((v) => v + 1);
    img.src = mockup.maskUrl.startsWith("http") ? mockup.maskUrl : `${API_BASE}${mockup.maskUrl}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockup.id]);

  /** Maskeden gosterim tuvalini bastan olustur (yukleme / temizleme / otomatik
   *  isaretleme sonrasi). Maske SIYAH ZEMIN uzerine beyaz isaret; zemin de opak
   *  oldugu icin alfa tabanli birlestirme (source-in) tum kareyi boyar — bu
   *  yuzden donusum PARLAKLIGA gore piksel piksel yapilir. */
  function rebuildDisplayFromMask() {
    const m = maskRef.current, d = dispRef.current;
    if (!m || !d) return;
    const W = d.width, H = d.height;
    const src = m.getContext("2d")!.getImageData(0, 0, W, H).data;
    const ctx = d.getContext("2d")!;
    const out = ctx.createImageData(W, H);
    if (preview) {
      // Gercek sonuc: maskenin beyaz oldugu yerde mockup pikseli on planda
      const bg = document.createElement("canvas");
      bg.width = W; bg.height = H;
      if (image) bg.getContext("2d")!.drawImage(image, 0, 0, W, H);
      const bgd = bg.getContext("2d")!.getImageData(0, 0, W, H).data;
      for (let i = 0; i < src.length; i += 4) {
        const a = src[i];                    // beyazlik = on plan gucu
        if (a < 8) continue;
        out.data[i] = bgd[i]; out.data[i + 1] = bgd[i + 1]; out.data[i + 2] = bgd[i + 2];
        out.data[i + 3] = a;
      }
    } else {
      for (let i = 0; i < src.length; i += 4) {
        const a = src[i];
        if (a < 8) continue;
        if (surfaceModeRef.current) { out.data[i] = 139; out.data[i + 1] = 92; out.data[i + 2] = 246; }
        else { out.data[i] = 239; out.data[i + 1] = 68; out.data[i + 2] = 68; }
        out.data[i + 3] = Math.round(a * 0.55);
      }
    }
    ctx.clearRect(0, 0, W, H);
    ctx.putImageData(out, 0, 0);

    // Alan icinde maskelenen oran (seyrek ornekleme) — tasarimi tamamen
    // ortecek kadar buyuduyse kullanici uyarilsin
    let inArea = 0, masked = 0;
    const step = 7;
    for (let y = 0; y < H; y += step) {
      for (let x = 0; x < W; x += step) {
        if (!areas.some((a) => pointInArea(x, y, a))) continue;
        inArea++;
        if (src[(y * W + x) * 4] > 40) masked++;
      }
    }
    setMaskCoverage(inArea > 0 ? Math.round((masked / inArea) * 100) : 0);
  }

  useEffect(() => {
    if (!dispRef.current) return;
    rebuildDisplayFromMask();
    setMaskVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, image, surfaceMode]);

  /** Cizimi tanimli alanlarin icine kis — alan disina tasan firca ise yaramaz */
  function clipToAreas(ctx: CanvasRenderingContext2D) {
    ctx.beginPath();
    for (const a of areas) {
      const rad = ((a.rotation || 0) * Math.PI) / 180;
      const cos = Math.cos(rad), sin = Math.sin(rad);
      const corners: [number, number][] = [[0, 0], [a.w, 0], [a.w, a.h], [0, a.h]];
      const pts = corners.map(([lx, ly]) => [a.x + lx * cos - ly * sin, a.y + lx * sin + ly * cos] as [number, number]);
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.closePath();
    }
    ctx.clip();
  }

  /** Bir firca darbesi — hem kaydedilen maskeye hem ekran gosterimine */
  function paintStroke(from: { x: number; y: number } | null, to: { x: number; y: number }) {
    if (surfaceEditId) {
      applyToSurface((ctx) => {
        ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.lineWidth = brushSize;
        ctx.beginPath();
        if (from) { ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke(); }
        ctx.beginPath(); ctx.arc(to.x, to.y, brushSize / 2, 0, Math.PI * 2); ctx.fill();
      }, !maskErase);
      return;
    }
    const m = maskRef.current, d = dispRef.current;
    if (!m || !d) return;
    for (const [canvas, mode] of [[m, "mask"], [d, "disp"]] as const) {
      const ctx = canvas.getContext("2d")!;
      ctx.save();
      if (!surfaceMode) clipToAreas(ctx);
      ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.lineWidth = brushSize;
      if (mode === "mask") {
        // Silgi maskede SIYAH boyar (zemin siyah = on planda degil)
        ctx.strokeStyle = maskErase ? "#000" : "#fff";
        ctx.fillStyle = ctx.strokeStyle;
      } else {
        ctx.globalCompositeOperation = maskErase ? "destination-out" : "source-over";
        ctx.strokeStyle = surfaceMode ? "rgba(139,92,246,0.55)" : "rgba(239,68,68,0.55)";
        ctx.fillStyle = ctx.strokeStyle;
      }
      ctx.beginPath();
      if (from) { ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke(); }
      ctx.beginPath();
      ctx.arc(to.x, to.y, brushSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    // Her fare hareketinde React render'i tetiklemek pahali — Konva katmanini
    // dogrudan tazele, durum bayraklari yalnizca degistiginde yazilsin.
    if (preview) { rebuildDisplayFromMask(); setMaskVersion((v) => v + 1); }
    maskImgRef.current?.getLayer()?.batchDraw();
    if (!maskDirty) setMaskDirty(true);
    if (!hasMask && !maskErase) setHasMask(true);
  }

  function clearMask() {
    const m = maskRef.current;
    if (!m) return;
    const ctx = m.getContext("2d")!;
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, m.width, m.height);
    surfaceModeRef.current = false;
    setSurfaceMode(false);
    rebuildDisplayFromMask();
    setMaskDirty(true);
    setHasMask(false);
    setMaskVersion((v) => v + 1);
  }

  /** PARLAK YERLERI ISARETLE — alan icindeki EN PARLAK %N pikseli on plana alir.
   *  Mutlak esik (orn. 210) acik renkli urunde alanin TAMAMINI gecerdi ve tasarim
   *  tamamen kaybolurdu; bu yuzden esik alanin kendi parlaklik dagilimindan
   *  (yuzdelik) hesaplanir — koyu ve acik kumasta ayni sekilde calisir. */
  function markBrightAreas() {
    const m = maskRef.current;
    if (!m || !image || areas.length === 0) return;
    const W = mockup.imageWidth, H = mockup.imageHeight;

    const src = document.createElement("canvas");
    src.width = W; src.height = H;
    const sctx = src.getContext("2d")!;
    sctx.drawImage(image, 0, 0, W, H);
    const data = sctx.getImageData(0, 0, W, H).data;

    // 1) Alan icindeki parlaklik histogrami
    const hist = new Uint32Array(256);
    let total = 0;
    for (const a of areas) {
      const bx0 = Math.max(0, Math.floor(a.x - Math.abs(a.h)));
      const bx1 = Math.min(W - 1, Math.ceil(a.x + a.w + Math.abs(a.h)));
      const by0 = Math.max(0, Math.floor(a.y - Math.abs(a.w)));
      const by1 = Math.min(H - 1, Math.ceil(a.y + a.h + Math.abs(a.w)));
      for (let y = by0; y <= by1; y++) {
        for (let x = bx0; x <= bx1; x++) {
          if (!pointInArea(x, y, a)) continue;
          const i = (y * W + x) * 4;
          const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
          hist[Math.min(255, Math.max(0, Math.round(lum)))]++;
          total++;
        }
      }
    }
    if (total === 0) return;

    // 2) En parlak %N'i verecek esigi bul
    const want = Math.max(1, Math.round((total * brightPercent) / 100));
    let acc = 0, cut = 255;
    for (let v = 255; v >= 0; v--) {
      acc += hist[v];
      if (acc >= want) { cut = v; break; }
    }

    // 3) Esigin ustundekileri maskeye yaz (alan disina tasmaz)
    const add = document.createElement("canvas");
    add.width = W; add.height = H;
    const actx = add.getContext("2d")!;
    const out = actx.createImageData(W, H);
    for (let i = 0; i < data.length; i += 4) {
      const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      if (lum < cut) continue;
      out.data[i] = 255; out.data[i + 1] = 255; out.data[i + 2] = 255; out.data[i + 3] = 255;
    }
    actx.putImageData(out, 0, 0);

    const mctx = m.getContext("2d")!;
    mctx.save();
    clipToAreas(mctx);
    mctx.globalCompositeOperation = "lighter";
    mctx.drawImage(add, 0, 0);
    mctx.restore();

    rebuildDisplayFromMask();
    setMaskDirty(true);
    setHasMask(true);
    setMaskVersion((v) => v + 1);
  }

  /** SAM oturumu — gorsel bir kez gomulur, sonraki istemler hizli */
  function getSam(): Promise<SamSession> {
    if (!samRef.current) {
      samRef.current = (async () => {
        if (!image) throw new Error("Mockup görseli henüz yüklenmedi");
        const { createSamSession } = await import("../../lib/sam-segment");
        return createSamSession(image, setAiStatus);
      })().catch((err) => {
        samRef.current = null;
        if (isStaleChunkError(err) && reloadForStaleChunk()) throw new Error("Panel güncellendi, sayfa yenileniyor…");
        throw err;
      });
    }
    return samRef.current;
  }

  /** Maskeyi yuzey moduna gecir: her yer on planda (beyaz), yuzeyler sonra oyulur */
  function startSurfaceMask() {
    const m = maskRef.current;
    if (!m) return;
    const c = m.getContext("2d")!;
    c.fillStyle = "#fff"; c.fillRect(0, 0, m.width, m.height);
    surfaceModeRef.current = true;
    setSurfaceMode(true);
  }

  /** Yuzey tuvalini (beyaz = yuzey) maskeye OYAR: yuzey siyah olur = tasarim gorunur */
  function carveSurface(surface: HTMLCanvasElement) {
    const m = maskRef.current;
    if (!m) return;
    if (!surfaceModeRef.current) startSurfaceMask();
    const tint = document.createElement("canvas");
    tint.width = m.width; tint.height = m.height;
    const tctx = tint.getContext("2d")!;
    tctx.drawImage(surface, 0, 0);
    tctx.globalCompositeOperation = "source-in";
    tctx.fillStyle = "#000"; tctx.fillRect(0, 0, tint.width, tint.height);
    m.getContext("2d")!.drawImage(tint, 0, 0);
  }

  async function runAi(task: (sam: SamSession) => Promise<number>) {
    setAiBusy(true);
    try {
      const sam = await getSam();
      setAiStatus("Yüzey aranıyor...");
      const found = await task(sam);
      if (found === 0) { setAiStatus("Yüzey bulunamadı — ürünün üstüne tıklayarak dene."); return; }
      rebuildDisplayFromMask();
      setMaskVersion((v) => v + 1);
      setMaskDirty(true);
      setHasMask(true);
      setAiStatus(`${found} yüzey bulundu. Mor yerlerde tasarım görünmez; fırça (gizle) / silgi (göster) ile düzeltebilirsin.`);
    } catch (err) {
      setAiStatus("AI maske hatası: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setAiBusy(false);
    }
  }

  /** OTOMATIK: her tasarim alaninin merkezinden urun yuzeyini bul */
  function autoSurface() {
    if (areas.length === 0) { setAiStatus("Önce ürünün üstüne bir tasarım alanı çiz."); return; }
    void runAi(async (sam) => {
      let found = 0;
      startSurfaceMask(); // yeniden olusturma: eski maske sifirlanir
      for (const a of areas) {
        const c = areaCenter(a);
        const surf = await sam.segment({ points: [c], expectedArea: a.w * a.h * SHAPE_FILL[a.shape ?? "rect"] });
        if (surf) { carveSurface(surf.canvas); found++; }
      }
      return found;
    });
  }

  /** TIKLA: tiklanan noktadaki yuzeyi ekle */
  function clickSurface(pt: { x: number; y: number }) {
    if (surfaceEditId) {
      // Alan yuzeyi: tiklanan nesne (or. ondeki tisortun kolu) gizlenir / eklenir
      const hide = !maskErase;
      setAiBusy(true);
      void getSam().then((sam) => sam.segment({ points: [pt] })).then((seg) => {
        if (!seg) { setAiStatus("Nesne bulunamadı — fırçayla düzelt."); return; }
        applyToSurface((ctx) => ctx.drawImage(seg.canvas, 0, 0), hide);
        rebuildSurfaceDisplay(); setSurfVersion((v) => v + 1);
        setAiStatus(hide ? "Seçilen parça bu alanın yüzeyinden çıkarıldı." : "Seçilen parça bu alanın yüzeyine eklendi.");
      }).catch((err) => setAiStatus("AI hatası: " + (err instanceof Error ? err.message : String(err))))
        .finally(() => setAiBusy(false));
      return;
    }
    void runAi(async (sam) => {
      const surf = await sam.segment({ points: [pt] });
      if (!surf) return 0;
      carveSurface(surf.canvas);
      return 1;
    });
  }

  /**
   * AI ILE OTOMATIK KUR — tek tus:
   *  1) sunucu (gorsel model) bos mockuptaki urunlerin kutularini bulur
   *  2) her urunun yuzeyi SAM ile cikarilir (delik/ip/arka plan disarida)
   *  3) alanlar maskenin sinirindan, sekil maskenin biciminden gelir
   *  4) maske + isik hazir; kullanici kontrol edip kaydeder
   * Anahtar yoksa ya da urun bulunamazsa ortadaki tek urun varsayilir.
   */
  async function autoSetup() {
    if (!image || aiBusy) return;
    setMaskMode(false);
    setAiClick(false);
    setAiBusy(true);
    try {
      setAiStatus("Ürünler aranıyor...");
      const W = mockup.imageWidth, H = mockup.imageHeight;
      let boxes: Array<{ x: number; y: number; w: number; h: number; surface?: "flat" | "cylinder" | "tilted" | "garment"; print?: { x: number; y: number; w: number; h: number } }> = [];
      let note = "";
      let occluders: Array<{ x: number; y: number; w: number; h: number }> = [];
      // Bu cagridan gelen urun tonlari (durum guncellemesi ayni fonksiyonda henuz okunamaz)
      let detTones: Array<{ tone: "light" | "mid" | "dark"; color?: string }> | null = null;
      try {
        // Giyim: urun tespiti + mockup analizi TEK AI cagrisinda
        const r = await apiClient.detectMockupProducts(mockup.id, { describe: !!apparel || isChart, model: detectModel });
        boxes = r.boxes ?? [];
        occluders = r.occluders ?? [];
        if (r.analysis) { draft.fill(r.analysis); detTones = r.analysis.items; }
        if (r.reason === "no_key") note = " (OpenRouter anahtarı yok — ortadaki tek ürün varsayıldı)";
      } catch { /* tek urun yedegi */ }
      if (boxes.length === 0) {
        boxes = [{ x: W * 0.25, y: H * 0.25, w: W * 0.5, h: H * 0.5 }];
        if (!note) note = " (ürün tespit edilemedi — ortadaki tek ürün varsayıldı)";
      }

      const sam = await getSam();
      setAiStatus(`${boxes.length} ürünün yüzeyi çıkarılıyor...`);
      startSurfaceMask();
      const next: AreaRect[] = [];
      const surfOf = new Map<string, HTMLCanvasElement>(); // alan id -> kendi urun yuzeyi (alfa)
      let garment = false;
      for (let i = 0; i < boxes.length; i++) {
        const b = boxes[i];
        // Giysi: baski kutusu yoksa govdenin ust-ortasi (yaka alti gogus)
        const pb = b.surface === "garment" ? (b.print ?? { x: b.x + b.w * 0.28, y: b.y + b.h * 0.2, w: b.w * 0.44, h: b.w * 0.44 / PRINT_RATIO }) : null;
        // SAM istemi gogusten: oturan/yan duran govdede kutu ortasi kol ya da bacak olabiliyor
        const seed = pb ? { x: pb.x + pb.w / 2, y: pb.y + pb.h / 2 } : { x: b.x + b.w / 2, y: b.y + b.h / 2 };
        const r = await sam.segment({ points: [seed], expectedArea: b.w * b.h * 0.8, focusBox: b });
        if (!r || r.box.w < 20 || r.box.h < 20) continue;
        carveSurface(r.canvas);
        const common = { id: `area-${Date.now()}-${i}`, label: `alan-${next.length + 1}`, theme: areas[next.length]?.theme ?? "light" } as const;
        surfOf.set(common.id, r.canvas);
        if (pb) {
          // Tisort/hoodie: alan baski oraninda (7:8) gogus kutusunun icine ortalanir;
          // maskenin sekli (kollar, alta daralan govde) alan sekli DEGILDIR
          const w = Math.min(pb.w, pb.h * PRINT_RATIO), h = w / PRINT_RATIO;
          next.push({ ...common, x: Math.round(pb.x + (pb.w - w) / 2), y: Math.round(pb.y), w: Math.round(w), h: Math.round(h), rotation: 0, shape: "rect" });
          garment = true;
        } else if (b.surface === "cylinder") {
          // Kupa/termos: maske kulpu da icerir -> alan modelin "baski yuzu"
          // kutusundan; egim maskenin alt kenar kavisinden
          next.push({ ...common, x: b.x, y: b.y, w: b.w, h: b.h, rotation: 0, shape: "rect", fit: "fill",
            // Alt kenar maskeden olculur; ust kenar kameraya daha yakin oldugu icin
            // daha az kavisli (yaklasik yarisi). Konik govde: daralma
            warp: { type: "cylinder", arc: 150, bendTop: Math.round(r.geo.bend * 50) / 100, bendBottom: Math.round(r.geo.bend * 100) / 100, taper: Math.round(r.geo.taper * 100) / 100 } });
        } else if (b.surface === "tilted") {
          // Paspas/tabak: alan maskenin sinir kutusu, koseler maskenin uc noktalari
          const base: AreaRect = { ...common, x: r.box.x, y: r.box.y, w: r.box.w, h: r.box.h, rotation: 0, shape: "rect", fit: "fill" };
          const rc = areaCorners(base);
          next.push({ ...base, warp: { type: "perspective", corners: r.geo.quad.map(([qx, qy], k) => [Math.round(qx - rc[k][0]), Math.round(qy - rc[k][1])] as [number, number]) } });
        } else if (r.shape === "rect" && Math.abs(r.geo.minRect.rotation) >= 1) {
          // Egik duran duz dikdortgen urun: en kucuk dondurulmus kutu
          const m = r.geo.minRect;
          next.push({ ...common, x: m.x, y: m.y, w: m.w, h: m.h, rotation: m.rotation, shape: "rect" });
        } else {
          next.push({ ...common, x: r.box.x, y: r.box.y, w: r.box.w, h: r.box.h, rotation: 0, shape: r.shape });
        }
      }
      if (next.length === 0) {
        setAiStatus("Yüzey çıkarılamadı. Alanı elle çizip Maske > AI yüzey'i dene.");
        return;
      }

      // ALAN BASINA YUZEY: tasarim yalnizca kendi urununde gorunur.
      //  - birden cok urun: arkadakinin tasarimi ondekinin ustune tasmasin
      //  - ustte duran parca (katli kol, el, sac, ondeki urunun tasan kismi):
      //    ortuldugu alanin yuzeyinden cikarilir. Alanin yarisindan fazlasini
      //    kaplayan parca o urunun KENDISIDIR (ondeki tisort) -> ona dokunulmaz.
      const used = new Set<string>(next.length > 1 ? next.map((a) => a.id) : []);
      if (occluders.length > 0) setAiStatus(`Üstte duran ${occluders.length} parça ayrılıyor (kol, el, öndeki ürün)...`);
      // Parca kimin? Kutusundaki pikselleri en cok hangi urunun yuzeyiyle ortusuyor
      const countIn = (c: HTMLCanvasElement, box: { x: number; y: number; w: number; h: number }, other?: HTMLCanvasElement) => {
        const x0 = Math.max(0, Math.floor(box.x)), y0 = Math.max(0, Math.floor(box.y));
        const w = Math.min(W - x0, Math.ceil(box.w)), h = Math.min(H - y0, Math.ceil(box.h));
        if (w <= 0 || h <= 0) return 0;
        const d = c.getContext("2d")!.getImageData(x0, y0, w, h).data;
        const e = other?.getContext("2d")!.getImageData(x0, y0, w, h).data;
        let n = 0;
        for (let i = 3; i < d.length; i += 16) if (d[i] > 128 && (!e || e[i] > 128)) n++;
        return n;
      };
      for (const o of occluders) {
        const seg = await sam.segment({ points: [], box: o, expectedArea: o.w * o.h * 0.6 });
        if (!seg) continue;
        const sctx = seg.canvas.getContext("2d")!;
        const segN = countIn(seg.canvas, o);
        // Segmentasyon basarisiz olunca maske KUTUNUN KENDISI donuyor: yuzeyden dikdortgen
        // oyuluyor, tasarim o dikdortgende siliniyordu (2026-10-12). Gercek sac/el/kol kutusunu
        // bu kadar doldurmaz -> kutunun %88'inden fazlasini dolduran parca yok sayilir.
        const boxN = Math.max(1, Math.ceil((Math.min(W, o.x + o.w) - Math.max(0, o.x)) * (Math.min(H, o.y + o.h) - Math.max(0, o.y)) / 4));
        if (segN / boxN > 0.88) continue;
        let ownerId: string | null = null, ownerN = 0;
        for (const a of next) {
          const sf = surfOf.get(a.id);
          const n = sf ? countIn(seg.canvas, o, sf) : 0;
          if (n > ownerN) { ownerN = n; ownerId = a.id; }
        }
        for (const a of next) {
          const surf = surfOf.get(a.id);
          if (!surf) continue;
          const x0 = Math.max(0, Math.floor(Math.min(a.x, a.x + a.w))), y0 = Math.max(0, Math.floor(a.y));
          const w = Math.min(W - x0, Math.ceil(a.w)), h = Math.min(H - y0, Math.ceil(a.h));
          if (w <= 0 || h <= 0) continue;
          const d = sctx.getImageData(x0, y0, w, h).data;
          let hit = 0, n = 0;
          for (let i = 3; i < d.length; i += 16) { n++; if (d[i] > 128) hit++; }
          const frac = hit / Math.max(1, n);
          if (frac < 0.01 || frac > 0.5) continue;
          // Baska urunun parcasi (ondeki tisortun kolu): yalnizca o urunle
          // KESISEN kismi cikar — SAM kutudaki bu urunun kumasini da secmis
          // olabilir, onu silmek tasarimi kendi urununde yok ediyordu.
          // Kendi parcasi (ayni tisortun katli kolu): parcanin tamami.
          let cut: CanvasImageSource = seg.canvas;
          const owner = ownerId && ownerId !== a.id && ownerN > segN * 0.3 ? surfOf.get(ownerId) : null;
          if (owner) {
            const inter = document.createElement("canvas");
            inter.width = W; inter.height = H;
            const ictx = inter.getContext("2d")!;
            ictx.drawImage(seg.canvas, 0, 0);
            ictx.globalCompositeOperation = "destination-in";
            ictx.drawImage(owner, 0, 0);
            cut = inter;
          }
          // Yerinde degistirilir: ortak maskeye oyma zaten yapildi
          const octx = surf.getContext("2d")!;
          octx.save();
          octx.globalCompositeOperation = "destination-out";
          octx.drawImage(cut, 0, 0);
          octx.restore();
          used.add(a.id);
        }
      }
      // ACIK/KOYU: urunun KENDI yuzeyindeki (alan icinde) ortalama parlaklik.
      // Kutunun tamami degil — arka plan / ondeki urun karismasin.
      // Tema = basilacak TASARIMIN rengi (Tasarimlar/Studio ile ayni): koyu urun
      // (kirmizi, yesil, siyah) -> "light" (acik renkli tasarim), acik urun -> "dark".
      {
        const pc = document.createElement("canvas");
        pc.width = W; pc.height = H;
        const pctx = pc.getContext("2d", { willReadFrequently: true })!;
        pctx.drawImage(image, 0, 0, W, H);
        for (let k = 0; k < next.length; k++) {
          const a = next[k];
          const surf = surfOf.get(a.id);
          const x0 = Math.max(0, Math.floor(a.x)), y0 = Math.max(0, Math.floor(a.y));
          const w = Math.min(W - x0, Math.ceil(a.w)), h = Math.min(H - y0, Math.ceil(a.h));
          if (w <= 1 || h <= 1) continue;
          const px = pctx.getImageData(x0, y0, w, h).data;
          const sm = surf?.getContext("2d")!.getImageData(x0, y0, w, h).data;
          let sum = 0, n = 0;
          for (let i = 0; i < px.length; i += 32) {
            if (sm && sm[i + 3] < 128) continue;
            sum += 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]; n++;
          }
          if (n > 20) next[k] = { ...a, theme: sum / n < 128 ? "light" : "dark" };
        }
        // Analizde urun tonlari varsa tema onlardan (tasarim rengi kurali)
        const tones = detTones ?? draft.items;
        if (apparel && tones.length === next.length) {
          for (let k = 0; k < next.length; k++) next[k] = { ...next[k], theme: themeForItem(tones[k], `${draft.brand} ${draft.model}`) };
        }
      }

      const nextSurfaces: Record<string, HTMLCanvasElement> = {};
      pendingSurfacesRef.current.clear();
      for (const id of used) {
        const c = surfOf.get(id);
        if (!c) continue;
        nextSurfaces[id] = c;
        pendingSurfacesRef.current.set(id, c);
      }
      setSurfaces(nextSurfaces);
      setAreas(next);
      setSelectedIdx(next.length === 1 ? 0 : -1);
      // Varsayilan efekt: 70/50 acik murekkebi ve ince yazilari solduruyordu (Comfort Colors mockuplari)
      if (shading === 0) setShading(45);
      if (garment && displace === 0) setDisplace(40);
      rebuildDisplayFromMask();
      setMaskVersion((v) => v + 1);
      setMaskDirty(true);
      setHasMask(true);
      const clippedN = Object.keys(nextSurfaces).length;
      setAiStatus(`${next.length} ürün hazır${clippedN ? `, ${clippedN} alan kendi ürününe kırpıldı` : ""}. Mor yerlerde tasarım görünmez. Kontrol et (P: önizleme), gerekirse düzelt ve Kaydet.${note}`);
    } catch (err) {
      setAiStatus("AI kurulum hatası: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setAiBusy(false);
    }
  }

  /** Ortaya hazir bir alan ekle (cizmeden) */
  function addArea() {
    if (areas.length >= maxAreas) return;
    const sideH = Math.round(Math.min(mockup.imageWidth, mockup.imageHeight) * 0.45);
    const sideW = Math.round(sideH * PRINT_RATIO);
    const usedNums = areas.map(a => { const m = /^alan-(\d+)$/.exec(a.label); return m ? Number(m[1]) : 0; });
    const a: AreaRect = {
      id: `area-${Date.now()}`,
      x: Math.round((mockup.imageWidth - sideW) / 2), y: Math.round((mockup.imageHeight - sideH) / 2),
      w: sideW, h: sideH, rotation: 0,
      label: `alan-${Math.max(0, ...usedNums) + 1}`, theme: "light",
    };
    setAreas(prev => [...prev, a]);
    setSelectedIdx(areas.length);
  }

  /** Nokta alanin icinde mi (dondurulmus alan icin ters dondurulur) */
  function pointInArea(px: number, py: number, a: AreaRect): boolean {
    let x = px - a.x, y = py - a.y;
    if (a.rotation) {
      const r = (-a.rotation * Math.PI) / 180;
      const c = Math.cos(r), sn = Math.sin(r);
      const nx = x * c - y * sn, ny = x * sn + y * c;
      x = nx; y = ny;
    }
    return x >= 0 && x <= a.w && y >= 0 && y <= a.h;
  }

  /** Maske tamamen bos mu (seyrek tarama) */
  function maskIsEmpty(): boolean {
    const m = maskRef.current;
    if (!m) return true;
    const d = m.getContext("2d")!.getImageData(0, 0, m.width, m.height).data;
    for (let i = 0; i < d.length; i += 4 * 97) if (d[i] > 12) return false;
    return true;
  }

  /** Alanlar kaydedilirken maske de kaydedilir — tek "Kaydet" yeterli olsun */
  async function persistMask() {
    if (!maskDirty || !maskRef.current) return;
    try {
      if (maskIsEmpty()) await apiClient.clearMockupOcclusion(mockup.id);
      else await apiClient.saveMockupOcclusion(mockup.id, maskRef.current.toDataURL("image/png"), mockup.occlusionFeather ?? 3);
      setMaskDirty(false);
    } catch (err) {
      alert("Ön plan maskesi kaydedilemedi: " + (err instanceof Error ? err.message : String(err)));
    }
  }

  /** Cokgeni kapat: icini secili moda gore doldur (Gizle = beyaz, Goster = siyah) */
  function closePolygon(raw = polyPts) {
    const m = maskRef.current;
    // Cift tiklama ayni yere iki nokta daha birakir: ust uste binenleri ayikla
    const tol = 4 / Math.max(0.01, scale);
    const pts = raw.filter((p, i) => i === 0 || Math.hypot(p.x - raw[i - 1].x, p.y - raw[i - 1].y) > tol);
    if (!m || pts.length < 3) return;
    if (surfaceEditId) {
      applyToSurface((c) => {
        c.beginPath();
        pts.forEach((p, i) => (i ? c.lineTo(p.x, p.y) : c.moveTo(p.x, p.y)));
        c.closePath(); c.fill();
      }, !maskErase);
      rebuildSurfaceDisplay(); setSurfVersion((v) => v + 1);
      setPolyPts([]); setPolyHover(null);
      return;
    }
    const ctx = m.getContext("2d")!;
    ctx.save();
    if (!surfaceMode) clipToAreas(ctx);
    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = maskErase ? "#000" : "#fff";
    ctx.fill();
    ctx.restore();
    setPolyPts([]);
    setPolyHover(null);
    rebuildDisplayFromMask();
    setMaskVersion((v) => v + 1);
    setMaskDirty(true);
    if (!maskErase) setHasMask(true);
  }

  // Cokgen klavyesi: Enter kapat, Esc iptal, Backspace son noktayi sil
  useEffect(() => {
    if (!maskMode || maskTool !== "polygon") return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName)) return;
      if (e.key === "Enter") { e.preventDefault(); closePolygon(); }
      else if (e.key === "Escape") { e.preventDefault(); setPolyPts([]); setPolyHover(null); }
      else if (e.key === "Backspace") { e.preventDefault(); setPolyPts((p) => p.slice(0, -1)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maskMode, maskTool, polyPts, maskErase, surfaceMode]);

  /** Gorseldeki tiklanan noktanin rengi (5x5 ortalama) -> etiket zemini; yazi zemine gore siyah/beyaz */
  function sampleLabelBg(pos: { x: number; y: number }) {
    if (!image) return;
    try {
      const c = document.createElement("canvas");
      c.width = 5; c.height = 5;
      const ctx = c.getContext("2d")!;
      ctx.drawImage(image, Math.round(pos.x) - 2, Math.round(pos.y) - 2, 5, 5, 0, 0, 5, 5);
      const d = ctx.getImageData(0, 0, 5, 5).data;
      let r = 0, g = 0, b = 0;
      for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; }
      const n = d.length / 4;
      [r, g, b] = [r / n, g / n, b / n].map(Math.round);
      const hex = `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
      const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      patchLabel({ bg: hex, bgOpacity: 1, textColor: lum > 0.55 ? "#1f1f1f" : "#ffffff" });
    } catch { /* gorsel CORS ile okunamadi */ }
  }

  function handleMouseDown(e: Konva.KonvaEventObject<MouseEvent>) {
    if (spaceHeld) return; // kaydirma modu: cizim/boya yok
    if (pickLabelBg) {
      const pos = e.target.getStage()?.getPointerPosition();
      if (pos) sampleLabelBg(pos);
      setPickLabelBg(false);
      return;
    }
    if (maskMode) {
      const pos = e.target.getStage()?.getPointerPosition();
      if (!pos) return;
      if (aiClick) { if (!aiBusy) clickSurface(pos); return; }
      if (maskTool === "polygon") {
        // Ilk noktaya yakin tiklama kapatir
        const near = polyPts.length >= 3 && Math.hypot(pos.x - polyPts[0].x, pos.y - polyPts[0].y) < 12 / Math.max(0.01, scale);
        if (near) closePolygon();
        else setPolyPts((p) => [...p, { x: pos.x, y: pos.y }]);
        return;
      }
      paintingRef.current = true;
      lastPtRef.current = pos;
      paintStroke(null, pos);
      return;
    }
    const stage = e.target.getStage();
    if (!stage) return;
    const clickedOnBg = e.target === stage || e.target.getClassName() === "Image";
    if (clickedOnBg) setLabelSelected(false);
    if (areas.length >= maxAreas) return;
    if (!clickedOnBg) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    setDrawing(true);
    setStartPos({ x: pos.x, y: pos.y });
    setDrawingRect({ x: pos.x, y: pos.y, w: 0, h: 0 });
    setSelectedIdx(-1);
  }

  function handleMouseMove(e: Konva.KonvaEventObject<MouseEvent>) {
    if (maskMode) {
      if (maskTool === "polygon" && !aiClick) {
        if (polyPts.length > 0) { const pos = e.target.getStage()?.getPointerPosition(); if (pos) setPolyHover({ x: pos.x, y: pos.y }); }
        return;
      }
      if (!paintingRef.current) return;
      const pos = e.target.getStage()?.getPointerPosition();
      if (!pos) return;
      paintStroke(lastPtRef.current, pos);
      lastPtRef.current = pos;
      return;
    }
    if (!drawing) return;
    const stage = e.target.getStage();
    const pos = stage?.getPointerPosition();
    if (!pos) return;
    const dx = Math.abs(pos.x - startPos.x);
    const dy = Math.abs(pos.y - startPos.y);
    // Baski alani orani (7:8 dikey) ile ciz
    const w = Math.max(dx, dy * PRINT_RATIO), h = w / PRINT_RATIO;
    setDrawingRect({
      x: pos.x < startPos.x ? startPos.x - w : startPos.x,
      y: pos.y < startPos.y ? startPos.y - h : startPos.y,
      w,
      h,
    });
  }

  function handleMouseUp() {
    if (maskMode) {
      paintingRef.current = false; lastPtRef.current = null;
      if (surfaceEditId) { rebuildSurfaceDisplay(); setSurfVersion((v) => v + 1); return; }
      // Darbe bitince kapsam oranini tazele (firca sirasinda hesaplamak pahali)
      rebuildDisplayFromMask();
      setMaskVersion((v) => v + 1);
      return;
    }
    if (!drawing) return;
    setDrawing(false);
    if (drawingRect.w > 20 && drawingRect.h > 20 && areas.length < maxAreas) {
      // Default to neutral numbered labels (Alan 1, Alan 2, ...) — body-part
      // names (On/Arka/Kol) are confusing for multi-product mockups (e.g. a
      // sheet of 6 shirts all printed on the front). Named labels stay
      // available in the dropdown for single-garment mockups.
      const usedNums = areas.map(a => { const m = /^alan-(\d+)$/.exec(a.label); return m ? Number(m[1]) : 0; });
      const nextLabel = `alan-${Math.max(0, ...usedNums) + 1}`;
      const newArea: AreaRect = {
        id: `area-${Date.now()}`,
        x: drawingRect.x,
        y: drawingRect.y,
        w: drawingRect.w,
        h: drawingRect.h,
        rotation: 0,
        label: nextLabel,
        theme: "light",
      };
      setAreas(prev => [...prev, newArea]);
      setSelectedIdx(areas.length);
    }
    setDrawingRect({ x: 0, y: 0, w: 0, h: 0 });
  }

  function removeArea(idx: number) {
    setAreas(prev => prev.filter((_, i) => i !== idx));
    setSelectedIdx(-1);
  }

  function updateAreaLabel(idx: number, label: string) {
    setAreas(prev => prev.map((a, i) => i === idx ? { ...a, label } : a));
  }

  function updateAreaWarp(idx: number, warp: AreaWarp | null) {
    setAreas(prev => prev.map((a, i) => i === idx ? { ...a, warp } : a));
  }

  function updateAreaShape(idx: number, shape: AreaShape) {
    setAreas(prev => prev.map((a, i) => i === idx ? { ...a, shape } : a));
  }

  function updateAreaTheme(idx: number, theme: "light" | "dark") {
    setAreas(prev => prev.map((a, i) => i === idx ? { ...a, theme } : a));
  }

  async function handleSave() {
    setSaving(true);
    try {
      // Derinlik bir kez: alanlar kaydedilince sunucu kirisigi onunla uretir.
      // Basarisizsa kaydetme durmaz, kirisik parlakliktan uretilir.
      if (displace > 0 && !depthDoneRef.current && image) {
        try {
          const { estimateDepth } = await import("../../lib/depth-estimate");
          await apiClient.saveMockupDepth(mockup.id, await estimateDepth(image, setAiStatus));
          depthDoneRef.current = true;
          setAiStatus("Derinlik haritası hazır; kumaş kırışığı onunla üretilecek.");
        } catch (err) {
          setAiStatus("Derinlik çıkarılamadı, kırışık parlaklıktan üretilecek: " + (err instanceof Error ? err.message : String(err)));
        }
      }
      await persistMask();
      // Isik once yazilir; ardindan alanlar kaydedilince sunucu haritayi
      // guncel alanlarla yeniden uretir
      if (shading !== (mockup.shading ?? 0) || displace !== (mockup.displace ?? 0)) {
        await apiClient.updateMockup(mockup.id, { shading, displace });
      }
      // Renk adi + etiket (degistiyse); ust bilesenin alan kaydi guncel kaydi geri okur
      if (colorName.trim() !== (mockup.color ?? "") || JSON.stringify(colorLabel) !== JSON.stringify(normalizeColorLabel(mockup.colorLabel))) {
        await apiClient.updateMockup(mockup.id, { color: colorName.trim() || null, colorLabel });
        if (colorLabel?.enabled) rememberColorLabel(colorLabel);
      }
      const saved = await areasWithUploadedSurfaces(areas);
      setAreas(saved);
      await onSave(saved.map(a => ({ ...a, x: Math.round(a.x), y: Math.round(a.y), w: Math.round(a.w), h: Math.round(a.h), rotation: +(a.rotation || 0).toFixed(2) })));
      // Giyim: analiz de ayni Kaydet ile
      if (apparel && draft.has) apparel.onSaved(await draft.save());
    } catch (err) {
      console.error("Design area save failed:", err);
    } finally {
      setSaving(false);
    }
  }

  const selectedArea = selectedIdx >= 0 ? areas[selectedIdx] : null;

  function nudgeRotation(delta: number) {
    if (selectedIdx < 0) return;
    setAreas((prev) => prev.map((a, i) => i === selectedIdx ? { ...a, rotation: +(((a.rotation || 0) + delta).toFixed(2)) } : a));
  }

  function nudgeArea(dx: number, dy: number) {
    setAreas((prev) => prev.map((a, i) => i === selectedIdx ? { ...a, x: Math.round(a.x + dx), y: Math.round(a.y + dy) } : a));
  }

  // Arrow keys move the selected area (Shift = 10px steps). Ignored while typing
  // in an input/select so the label dropdown still works normally.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (selectedIdx < 0) return;
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName)) return;
      // A / D rotate the area under the mouse (fallback: selected). Shift = fine 0.2 deg.
      const k = e.key.toLowerCase();
      if (k === "a" || k === "d") {
        const target = hoveredIdxRef.current >= 0 ? hoveredIdxRef.current : selectedIdx;
        if (target < 0) return;
        e.preventDefault();
        const delta = (k === "a" ? -1 : 1) * (e.shiftKey ? 0.2 : 1);
        setAreas((prev) => prev.map((a, i) => i === target ? { ...a, rotation: +(((a.rotation || 0) + delta).toFixed(2)) } : a));
        return;
      }
      const step = e.shiftKey ? 10 : 1;
      let dx = 0, dy = 0;
      if (e.key === "ArrowLeft") dx = -step;
      else if (e.key === "ArrowRight") dx = step;
      else if (e.key === "ArrowUp") dy = -step;
      else if (e.key === "ArrowDown") dy = step;
      else return;
      e.preventDefault();
      nudgeArea(dx, dy);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdx]);

  const SHAPE_ICON = { rect: Square, ellipse: Circle, heart: Heart } as const;
  const sectionTitle = "text-[11px] font-semibold uppercase tracking-wide text-gray-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div className="flex w-full max-w-[1500px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" style={{ maxHeight: "95vh" }}>
        {/* Baslik */}
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-900">Mockup şablonunu kur</h2>
            <p className="truncate text-xs text-gray-500">
              <span className="font-medium text-gray-700">{mockup.label}</span> · {mockup.imageWidth}×{mockup.imageHeight}
            </p>
          </div>
          <button onClick={onCancel} className="rounded-lg p-2 transition-colors hover:bg-gray-100" title="Kapat">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* ===== Tuval ===== */}
          <div className="flex min-w-0 flex-1 flex-col bg-gray-50">
            <div className="flex items-center gap-1.5 border-b border-gray-100 bg-white px-4 py-2">
              <span className="mr-auto truncate text-[11px] text-gray-500">
                {maskMode
                  ? aiClick
                    ? "Tıkla-seç açık: ürünün üstüne tıkla, yüzeyi eklenir."
                    : maskTool === "polygon"
                      ? `Çokgen: noktaları tıkla, ilk noktaya tıkla / çift tıkla / Enter ile kapat — alan ${maskErase ? "GÖSTERİLİR" : "GİZLENİR"}.`
                      : maskErase ? "Silgi: sürdüğün yerde tasarım GÖRÜNÜR." : "Fırça: sürdüğün yerde tasarım GİZLENİR."
                  : preview && areas.length > 0
                    ? (finishing ? "Işık ve kırışık uygulanıyor..." : "Önizleme: sonuçtaki gibi görünür (ışık + kırışık dahil) · Sürükle: taşı · Tekerlek: boyut · P: ayarlara dön")
                  : areas.length === 0
                    ? "Sağdaki AI ile otomatik kur'a bas ya da ürünün üstünde sürükleyerek alan çiz."
                    : zoom > 1.01
                      ? "Boşluk + sürükle: kaydır · Ctrl + tekerlek: yakınlaş/uzaklaş · F: alana odaklan"
                      : "Sürükle: taşı · Köşeler: boyut · A-D: döndür · Ctrl + tekerlek: yakınlaş · F: alana odaklan"}
              </span>
              <button type="button" onClick={() => setPreview((v) => !v)} disabled={areas.length === 0}
                title="Mor alanı, ızgarayı ve tutamaçları gizle; tasarımı sonuçtaki gibi gör (sürükleyerek taşımak açık) — kısayol: P"
                className={`mr-1 flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-medium disabled:opacity-40 ${preview ? "bg-gray-900 text-white hover:bg-gray-800" : "border border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
                <Eye className="h-3.5 w-3.5" /> {preview ? "Ayarları göster" : "Önizleme"}
              </button>
              <button type="button" onClick={focusAreas} disabled={areas.length === 0}
                title="Seçili alana (yoksa tüm alanlara) yakınlaş — kısayol: F"
                className="mr-1 flex h-7 items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-40">
                <ZoomIn className="h-3.5 w-3.5" /> Alana yakınlaş
              </button>
              <button type="button" onClick={() => zoomTo(zoom / 1.25)}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50" title="Uzaklaştır (Ctrl + tekerlek)">
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="w-11 text-center text-[11px] tabular-nums text-gray-600">{Math.round(scale * 100)}%</span>
              <button type="button" onClick={() => zoomTo(zoom * 1.25)}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50" title="Yakınlaştır (Ctrl + tekerlek)">
                <Plus className="h-3.5 w-3.5" />
              </button>
              <button type="button" onClick={() => { anchorRef.current = null; setZoom(1); }}
                className="ml-1 h-7 rounded-md border border-gray-200 px-2 text-[11px] text-gray-600 hover:bg-gray-50">Sığdır</button>
            </div>
            <div ref={scrollRef}
              className="relative flex flex-1 overflow-auto p-4"
              style={{ cursor: spaceHeld ? (panRef.current ? "grabbing" : "grab") : undefined }}
              onMouseDownCapture={(e) => {
                if (!spaceHeld || !scrollRef.current) return;
                e.preventDefault(); e.stopPropagation();
                panRef.current = { x: e.clientX, y: e.clientY, sl: scrollRef.current.scrollLeft, st: scrollRef.current.scrollTop };
              }}
              onMouseMoveCapture={(e) => {
                const p = panRef.current, el = scrollRef.current;
                if (!p || !el) return;
                e.stopPropagation();
                el.scrollLeft = p.sl - (e.clientX - p.x);
                el.scrollTop = p.st - (e.clientY - p.y);
              }}
              onMouseUpCapture={() => { panRef.current = null; }}
              onMouseLeave={() => { panRef.current = null; }}>
              <div className="relative m-auto shrink-0 overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                <div style={{ width: canvasW, height: canvasH }}>
                  <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
                <Stage
                  width={mockup.imageWidth}
                  height={mockup.imageHeight}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onDblClick={() => { if (maskMode && maskTool === "polygon" && !aiClick) closePolygon(); }}
                  style={{ cursor: spaceHeld ? "inherit" : pickLabelBg ? "copy" : maskMode ? "cell" : drawing ? "crosshair" : areas.length < maxAreas ? "crosshair" : "default" }}
                >
                  <Layer>
                    {image && (
                      <KonvaImage image={image} x={0} y={0} width={mockup.imageWidth} height={mockup.imageHeight} />
                    )}
                    {/* Overlay with holes for all areas */}
                    {areas.length > 0 && !surfaceMode && !preview && (
                      <Shape
                        sceneFunc={(context) => {
                          const ctx = context._context as CanvasRenderingContext2D;
                          const iw = mockup.imageWidth;
                          const ih = mockup.imageHeight;
                          ctx.beginPath();
                          ctx.moveTo(0, 0);
                          ctx.lineTo(iw, 0);
                          ctx.lineTo(iw, ih);
                          ctx.lineTo(0, ih);
                          ctx.closePath();
                          for (const area of areas) {
                            const rad = ((area.rotation || 0) * Math.PI) / 180;
                            const cos = Math.cos(rad);
                            const sin = Math.sin(rad);
                            const corners = [[0, 0], [0, area.h], [area.w, area.h], [area.w, 0]];
                            const rotated = corners.map(([lx, ly]) => [
                              area.x + lx * cos - ly * sin,
                              area.y + lx * sin + ly * cos,
                            ]);
                            ctx.moveTo(rotated[0][0], rotated[0][1]);
                            for (let i = 1; i < rotated.length; i++) {
                              ctx.lineTo(rotated[i][0], rotated[i][1]);
                            }
                            ctx.closePath();
                          }
                          ctx.fillStyle = "rgba(0,0,0,0.3)";
                          ctx.fill("evenodd");
                        }}
                        listening={false}
                      />
                    )}
                    {/* Test/sample design preview inside each area (if uploaded) */}
                    {showFinished && finished && (
                      <KonvaImage image={finished.canvas} x={0} y={0} width={mockup.imageWidth} height={mockup.imageHeight} listening={false} />
                    )}
                    {!showFinished && areas.map((area, idx) => {
                      const tUrl = (area.theme ?? "light") === "dark" ? (testDesignDark ?? testDesignLight) : (testDesignLight ?? testDesignDark);
                      return tUrl ? <AreaTestImage key={`test-${area.id || idx}`} url={tUrl} area={area} opacity={mockup.opacity / 100}
                        surface={usableSurface(area) ?? undefined} surfVersion={surfVersion} imageW={mockup.imageWidth} imageH={mockup.imageHeight} /> : null;
                    })}
                    {/* Area rects */}
                    {areas.map((area, idx) => {
                      const color = AREA_COLORS[idx % AREA_COLORS.length];
                      return (
                        <Shape
                          key={area.id}
                          ref={el => { rectRefs.current[idx] = el; }}
                          x={area.x} y={area.y} width={area.w} height={area.h}
                          rotation={area.rotation}
                          sceneFunc={(context, node) => {
                            // Sekil cercevesi + icine kirpilmis hizalama izgarasi
                            // (orta cizgi deligin/askinin ortasindan gecsin diye kalin)
                            const ctx = context._context as CanvasRenderingContext2D;
                            const w = node.width(), h = node.height();
                            const sel = selectedIdx === idx;
                            // Onizleme: cerceve/izgara yok, yalniz tasarim (surukleme hitFunc ile surer)
                            if (preview) return;
                            if (hasWarp(area) || area.warp?.type === "perspective") {
                              // Carpitilmis alan: tutamac kutusu ince kesikli; asil
                              // izgara ayri katmanda carpitilmis cizilir
                              ctx.save();
                              ctx.setLineDash([8 / Math.max(0.01, scale), 6 / Math.max(0.01, scale)]);
                              ctx.strokeStyle = color.stroke; ctx.globalAlpha = sel ? 0.7 : 0.35;
                              ctx.lineWidth = 1 / Math.max(0.01, scale);
                              ctx.strokeRect(0, 0, w, h);
                              ctx.restore();
                              return;
                            }
                            ctx.save();
                            traceAreaShape(ctx, area.shape, w, h);
                            if (sel) { ctx.fillStyle = color.fill; ctx.fill(); }
                            if (isShaped(area) || sel) {
                              ctx.save();
                              ctx.clip();
                              ctx.strokeStyle = color.stroke;
                              const n = 8;
                              for (let k = 1; k < n; k++) {
                                ctx.globalAlpha = k === n / 2 ? 0.9 : 0.35;
                                ctx.lineWidth = (k === n / 2 ? 2 : 1) / Math.max(0.01, scale);
                                ctx.beginPath(); ctx.moveTo((w * k) / n, 0); ctx.lineTo((w * k) / n, h); ctx.stroke();
                                ctx.beginPath(); ctx.moveTo(0, (h * k) / n); ctx.lineTo(w, (h * k) / n); ctx.stroke();
                              }
                              ctx.restore();
                            }
                            traceAreaShape(ctx, area.shape, w, h);
                            ctx.globalAlpha = 1;
                            ctx.strokeStyle = color.stroke;
                            ctx.lineWidth = 2 / Math.max(0.01, scale);
                            ctx.stroke();
                            ctx.restore();
                          }}
                          hitFunc={(context, node) => {
                            context.beginPath();
                            context.rect(0, 0, node.width(), node.height());
                            context.closePath();
                            context.fillStrokeShape(node);
                          }}
                          draggable={!drawing && !maskMode}
                          onClick={() => setSelectedIdx(idx)}
                          onTap={() => setSelectedIdx(idx)}
                          onMouseEnter={() => { hoveredIdxRef.current = idx; }}
                          onMouseLeave={() => { if (hoveredIdxRef.current === idx) hoveredIdxRef.current = -1; }}
                          onWheel={(e) => {
                            // Mouse wheel over an area grows/shrinks it around its center.
                            // Ctrl/Cmd + tekerlek ise tuvali yakinlastirir (alan degismez)
                            if (e.evt.ctrlKey || e.evt.metaKey) return;
                            e.evt.preventDefault();
                            setSelectedIdx(idx);
                            const factor = e.evt.deltaY < 0 ? 1.05 : 0.95;
                            setAreas((prev) => prev.map((a, i) => {
                              if (i !== idx) return a;
                              const newW = Math.max(30, a.w * factor);
                              const newH = Math.max(30, a.h * factor);
                              return { ...a, w: newW, h: newH, x: a.x + (a.w - newW) / 2, y: a.y + (a.h - newH) / 2 };
                            }));
                          }}
                          onDragMove={(e) => {
                            // Carpitilmis izgara ayri katmanda / onizlemede cerceve yok:
                            // tasarim surukleme sirasinda da izlesin
                            if (!area.warp && !preview) return;
                            setAreas(prev => prev.map((a, i) => i === idx ? { ...a, x: e.target.x(), y: e.target.y() } : a));
                          }}
                          onDragEnd={(e) => {
                            setAreas(prev => prev.map((a, i) => i === idx ? { ...a, x: e.target.x(), y: e.target.y() } : a));
                          }}
                          onTransformEnd={() => {
                            const node = rectRefs.current[idx];
                            if (!node) return;
                            const sx = node.scaleX();
                            const sy = node.scaleY();
                            node.scaleX(1);
                            node.scaleY(1);
                            const newW = Math.max(30, node.width() * sx);
                            const newH = Math.max(30, node.height() * sy);
                            // Sekilli alan serbest oranli (egik duran daire = elips);
                            // dikdortgen baski alani oraninda (7:8) kalir
                            const free = isFreeAspect(area);
                            const ph = Math.max(newH, newW / PRINT_RATIO);
                            const fw = free ? newW : ph * PRINT_RATIO, fh = free ? newH : ph;
                            node.width(fw);
                            node.height(fh);
                            setAreas(prev => prev.map((a, i) => i === idx ? { ...a, x: node.x(), y: node.y(), w: fw, h: fh, rotation: node.rotation() } : a));
                          }}
                        />
                      );
                    })}
                    {/* Area labels — color follows theme; hidden when a sample design is shown */}
                    {areas.map((area) => {
                      const tUrl = (area.theme ?? "light") === "dark" ? (testDesignDark ?? testDesignLight) : (testDesignLight ?? testDesignDark);
                      if (tUrl || isShaped(area) || preview) return null;
                      const light = (area.theme ?? "light") === "light";
                      return (
                        <Text
                          key={`lbl-${area.id}`}
                          x={area.x} y={area.y} width={area.w} height={area.h} rotation={area.rotation}
                          text={areaLabelText(area.label).toUpperCase()}
                          align="center" verticalAlign="middle"
                          fontSize={Math.max(16, Math.min(area.w, area.h) * 0.16)} fontStyle="bold"
                          fill={light ? "#ffffff" : "#000000"}
                          shadowColor={light ? "#000000" : "#ffffff"} shadowBlur={4} shadowOpacity={0.55}
                          listening={false}
                        />
                      );
                    })}
                    {/* Carpitilmis alanlarin izgarasi (ortak esleme ile, gorsel koordinatinda) */}
                    {areas.map((area, idx) => {
                      if (!area.warp || preview) return null;
                      const color = AREA_COLORS[idx % AREA_COLORS.length];
                      return (
                        <Shape key={`warp-${area.id}`} listening={false}
                          sceneFunc={(context) => {
                            const ctx = context._context as CanvasRenderingContext2D;
                            const map = warpMapOf(area);
                            const lw = 1 / Math.max(0.01, scale);
                            const line = (pts: [number, number][]) => {
                              ctx.beginPath();
                              pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
                              ctx.stroke();
                            };
                            const N = 24, G = 8;
                            ctx.save();
                            ctx.strokeStyle = color.stroke;
                            for (let k = 1; k < G; k++) {
                              const t = k / G;
                              ctx.globalAlpha = k === G / 2 ? 0.9 : 0.35;
                              ctx.lineWidth = (k === G / 2 ? 2 : 1) * lw;
                              line(Array.from({ length: N + 1 }, (_, i) => map.forward(t, i / N)));
                              line(Array.from({ length: N + 1 }, (_, i) => map.forward(i / N, t)));
                            }
                            ctx.globalAlpha = 1;
                            ctx.lineWidth = 2 * lw;
                            const edge: [number, number][] = [
                              ...Array.from({ length: N + 1 }, (_, i) => map.forward(i / N, 0)),
                              ...Array.from({ length: N + 1 }, (_, i) => map.forward(1, i / N)),
                              ...Array.from({ length: N + 1 }, (_, i) => map.forward(1 - i / N, 1)),
                              ...Array.from({ length: N + 1 }, (_, i) => map.forward(0, 1 - i / N)),
                            ];
                            line([...edge, edge[0]]);
                            ctx.restore();
                          }}
                        />
                      );
                    })}
                    {/* Silindir tutamaclari (secili alan): ust/alt egim + daralma */}
                    {!maskMode && !preview && selectedArea?.warp?.type === "cylinder" && (() => {
                      const a = selectedArea;
                      const cp = cylinderParams(a.warp as Parameters<typeof cylinderParams>[0]);
                      const map = warpMapOf(a);
                      const rad = ((a.rotation || 0) * Math.PI) / 180, c = Math.cos(rad), sn = Math.sin(rad);
                      const toLocal = (x: number, y: number) => { const dx = x - a.x, dy = y - a.y; return [dx * c + dy * sn, -dx * sn + dy * c]; };
                      const R = 10 / Math.max(0.01, scale), SW = 2.5 / Math.max(0.01, scale);
                      const handle = (key: string, pos: [number, number], title: string, onMove: (lx: number, ly: number) => void) => (
                        <KonvaCircle key={key} x={pos[0]} y={pos[1]} radius={R} fill="#fb923c" stroke="#ffffff" strokeWidth={SW}
                          shadowColor="#000" shadowBlur={4 / Math.max(0.01, scale)} shadowOpacity={0.35} draggable
                          onMouseEnter={(e) => { const st = e.target.getStage(); if (st) { st.container().style.cursor = "grab"; st.container().title = title; } }}
                          onMouseLeave={(e) => { const st = e.target.getStage(); if (st) { st.container().style.cursor = ""; st.container().title = ""; } }}
                          onDragMove={(e) => { const [lx, ly] = toLocal(e.target.x(), e.target.y()); onMove(lx, ly); }}
                          onDragEnd={(e) => { const p = map.forward(key === "taper" ? 1 : 0.5, key === "top" ? 0 : 1); e.target.position({ x: p[0], y: p[1] }); }}
                        />
                      );
                      const upd = (p: Partial<typeof cp>) => setAreas((prev) => prev.map((x, i) => (i === selectedIdx ? { ...x, warp: { type: "cylinder", ...cp, ...p } } : x)));
                      const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
                      return (
                        <>
                          {handle("top", map.forward(0.5, 0), "Üst kenar kavisi: yukarı/aşağı sürükle", (_lx, ly) => upd({ bendTop: +clamp(ly / a.h, -0.3, 0.3).toFixed(3) }))}
                          {handle("bottom", map.forward(0.5, 1), "Alt kenar kavisi: yukarı/aşağı sürükle", (_lx, ly) => upd({ bendBottom: +clamp((ly - a.h) / a.h, -0.3, 0.3).toFixed(3) }))}
                          {handle("taper", map.forward(1, 1), "Daralma: sağa/sola sürükle", (lx) => upd({ taper: +clamp((2 * lx - a.w) / a.w, 0.6, 1.4).toFixed(3) }))}
                        </>
                      );
                    })()}
                    {/* On plan maskesi — kirmizi alanlarda tasarim silinir, mockup on planda kalir */}
                    {surfaceEditId && surfDispRef.current && (
                      <KonvaImage key={`surf-${surfVersion}`} ref={(el) => { surfImgRef.current = el; }} image={surfDispRef.current}
                        x={0} y={0} width={mockup.imageWidth} height={mockup.imageHeight} listening={false} />
                    )}
                    {dispRef.current && !surfaceEditId && (
                      <KonvaImage
                        key={`mask-${maskVersion}`}
                        ref={(el) => { maskImgRef.current = el; }}
                        image={dispRef.current}
                        x={0} y={0}
                        width={mockup.imageWidth} height={mockup.imageHeight}
                        listening={false}
                      />
                    )}
                    {/* Cokgen onizlemesi (maske araci) */}
                    {maskMode && maskTool === "polygon" && polyPts.length > 0 && (() => {
                      const lw = 2 / Math.max(0.01, scale);
                      const col = maskErase ? "#10b981" : "#f59e0b";
                      const pts = polyPts.flatMap((p) => [p.x, p.y]);
                      return (
                        <>
                          <KonvaLine points={[...pts, ...(polyHover ? [polyHover.x, polyHover.y] : [])]} stroke={col} strokeWidth={lw}
                            dash={[8 / scale, 5 / scale]} closed={polyPts.length >= 3} fill={polyPts.length >= 3 ? (maskErase ? "rgba(16,185,129,0.18)" : "rgba(245,158,11,0.22)") : undefined} listening={false} />
                          {polyPts.map((p, i) => (
                            <KonvaCircle key={i} x={p.x} y={p.y} radius={(i === 0 ? 7 : 5) / Math.max(0.01, scale)}
                              fill={i === 0 ? col : "#ffffff"} stroke={col} strokeWidth={lw} listening={false} />
                          ))}
                        </>
                      );
                    })()}
                    {/* Drawing preview */}
                    {drawing && drawingRect.w > 3 && (
                      <Rect
                        x={drawingRect.x} y={drawingRect.y} width={drawingRect.w} height={drawingRect.h}
                        stroke="#6366f1" strokeWidth={2} dash={[6, 3]}
                        fill="rgba(99,102,241,0.08)" listening={false}
                      />
                    )}
                    {/* Transformer for selected */}
                    {!drawing && !preview && selectedIdx >= 0 && (
                      <Transformer
                        ref={transformerRef}
                        keepRatio={!(selectedArea && isFreeAspect(selectedArea))}
                        rotateEnabled={true}
                        rotationSnaps={[]}
                        rotationSnapTolerance={0}
                        // Perspektifte koseler yuvarlak tutamaclarda; kare kose tutamaclari ustlerine biniyordu
                        enabledAnchors={selectedArea?.warp?.type === "perspective"
                          ? ["top-center","middle-left","middle-right","bottom-center"]
                          : selectedArea && isFreeAspect(selectedArea)
                          ? ["top-left","top-center","top-right","middle-left","middle-right","bottom-left","bottom-center","bottom-right"]
                          : ["top-left","top-right","bottom-left","bottom-right"]}
                        boundBoxFunc={(o, n) => (n.width < 30 || n.height < 30 ? o : n)}
                        anchorSize={14 / Math.max(0.01, scale)}
                        anchorCornerRadius={3}
                        anchorStroke={AREA_COLORS[selectedIdx % AREA_COLORS.length]?.stroke || "#6366f1"}
                        anchorFill="#fff"
                        anchorStrokeWidth={2 / Math.max(0.01, scale)}
                        borderStroke={AREA_COLORS[selectedIdx % AREA_COLORS.length]?.stroke || "#6366f1"}
                        borderStrokeWidth={2 / Math.max(0.01, scale)}
                        rotateAnchorOffset={36 / Math.max(0.01, scale)}
                        rotateAnchorCursor="grab"
                        padding={0}
                      />
                    )}
                    {/* Perspektif kose tutamaclari (secili alan) — Transformer'dan sonra: ustte kalsin */}
                    {!maskMode && !preview && selectedArea?.warp?.type === "perspective" && perspectiveQuad(selectedArea).map(([qx, qy], ci) => (
                      <KonvaCircle key={`pq-${ci}`} x={qx} y={qy} radius={9 / Math.max(0.01, scale)}
                        fill="#ffffff" stroke={AREA_COLORS[selectedIdx % AREA_COLORS.length]?.stroke || "#6366f1"}
                        strokeWidth={2.5 / Math.max(0.01, scale)} draggable
                        onMouseEnter={(e) => { const st = e.target.getStage(); if (st) st.container().style.cursor = "move"; }}
                        onMouseLeave={(e) => { const st = e.target.getStage(); if (st) st.container().style.cursor = ""; }}
                        onDragMove={(e) => {
                          const nx = e.target.x(), ny = e.target.y();
                          setAreas(prev => prev.map((a, i) => {
                            if (i !== selectedIdx || a.warp?.type !== "perspective") return a;
                            const base = areaCorners(a)[ci];
                            const corners = a.warp.corners.map((c) => [...c] as [number, number]);
                            corners[ci] = [Math.round(nx - base[0]), Math.round(ny - base[1])];
                            return { ...a, warp: { type: "perspective", corners } };
                          }));
                        }}
                      />
                    ))}
                    {/* Renk etiketi: surukle = tasi, kose tutamaci = boyut */}
                    {!maskMode && labelBox && colorLabel && (
                      <Group ref={labelRef} x={labelBox.x} y={labelBox.y} draggable
                        onMouseDown={(e) => { e.cancelBubble = true; setLabelSelected(true); setSelectedIdx(-1); }}
                        onMouseEnter={(e) => { const st = e.target.getStage(); if (st) st.container().style.cursor = "move"; }}
                        onMouseLeave={(e) => { const st = e.target.getStage(); if (st) st.container().style.cursor = ""; }}
                        onDragEnd={(e) => patchLabel({ x: e.target.x() / mockup.imageWidth, y: e.target.y() / mockup.imageHeight })}
                        onTransformEnd={(e) => {
                          const n = e.target; const k = n.scaleY();
                          n.scaleX(1); n.scaleY(1);
                          patchLabel({ size: colorLabel.size * k, x: n.x() / mockup.imageWidth, y: n.y() / mockup.imageHeight });
                        }}>
                        {colorLabel.shape === "brush" ? (
                          <KonvaPath data={colorLabelBrushPath(labelBox.w, labelBox.h)} fill={colorLabel.bg} opacity={colorLabel.bgOpacity} />
                        ) : colorLabel.shape !== "none" && (
                          <Rect width={labelBox.w} height={labelBox.h} cornerRadius={labelBox.radius}
                            fill={colorLabel.bg} opacity={colorLabel.bgOpacity} />
                        )}
                        <Text text={labelText} x={labelBox.pad} y={0} height={labelBox.h} verticalAlign="middle"
                          fontSize={labelBox.fontSize} fontFamily="Montserrat" fontStyle="600" fill={colorLabel.textColor} listening={false} />
                      </Group>
                    )}
                    {!maskMode && labelBox && (
                      <Transformer ref={labelTrRef} keepRatio rotateEnabled={false}
                        enabledAnchors={["top-left", "top-right", "bottom-left", "bottom-right"]}
                        boundBoxFunc={(o, n) => (n.height < 12 ? o : n)}
                        anchorSize={12 / Math.max(0.01, scale)} anchorStroke="#6366f1" anchorFill="#fff"
                        anchorStrokeWidth={2 / Math.max(0.01, scale)} borderStroke="#6366f1"
                        borderStrokeWidth={2 / Math.max(0.01, scale)} padding={4 / Math.max(0.01, scale)} />
                    )}
                  </Layer>
                </Stage>
                  </div>
                </div>
                {aiBusy && (
                  <div className="pointer-events-none absolute inset-0 grid place-items-center bg-white/40 backdrop-blur-[1px]">
                    <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-medium text-violet-700 shadow">
                      <Loader2 className="h-4 w-4 animate-spin" /> {aiStatus || "Çalışıyor..."}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ===== Sag panel ===== */}
          <aside className="flex w-[360px] shrink-0 flex-col border-l border-gray-100">
            <div className="flex-1 space-y-5 overflow-y-auto p-4">
              {/* 1. AI ile kur */}
              <section className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-white p-3.5">
                <button onClick={() => void autoSetup()} disabled={aiBusy || !image}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-violet-700 disabled:opacity-60">
                  {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  AI ile otomatik kur
                </button>
                <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                  Model
                  <select value={detectModel} onChange={(e) => pickDetectModel(e.target.value)} disabled={aiBusy} aria-label="AI ile kur modeli"
                    className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    {MOCKUP_DETECT_MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                </label>
                <p className="mt-2 text-[11px] leading-relaxed text-gray-600">
                  Ürünleri bulur, tasarım alanını ve şeklini yerleştirir; delik, ip ve arka planı maskeler.
                  Hediyelik ürünler için (ornament, kupa, tabak, coin...).
                </p>
                {aiStatus && !aiBusy && (
                  <p className="mt-2 rounded-md border border-violet-100 bg-white px-2.5 py-1.5 text-[11px] leading-relaxed text-violet-900">{aiStatus}</p>
                )}
              </section>

              {/* Giyim mockup analizi: AI ile otomatik kur doldurur, Kaydet ile alanlarla birlikte kaydedilir */}
              {apparel && (
                <section className="rounded-xl border border-gray-200 p-3.5">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className={sectionTitle}>Mockup analizi</h3>
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
                      <input type="checkbox" checked={draft.approved} onChange={(e) => draft.setApproved(e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" /> Onaylı
                    </label>
                  </div>
                  <p className="mb-3 text-[11px] leading-relaxed text-gray-600">
                    {draft.has ? "Kontrol et, gerekirse düzelt; Kaydet alanlarla birlikte kaydeder. Ürün tonları alanların açık/koyu tasarım temasını belirler." : "AI ile otomatik kur alanlarla birlikte bunu da doldurur."}
                  </p>
                  {!draft.has && (
                    <button onClick={() => void runAnalysis()} disabled={analyzing}
                      className="mb-3 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                      {analyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-indigo-600" />} Alanlar hazırsa yalnız analiz et
                    </button>
                  )}
                  <MockupAnalysisFields d={draft} areaCount={areas.length} compact />
                </section>
              )}

              {/* 2. Alanlar */}
              <section>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className={sectionTitle}>Tasarım alanları</h3>
                  <span className="flex items-center gap-2">
                    {areas.length > 1 && (
                      <button type="button" onClick={equalizeToFirst}
                        title="Tüm alanların genişlik ve yüksekliği 1. alanla aynı olur (merkezleri yerinde kalır)"
                        className="rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50">
                        İlk alana eşitle
                      </button>
                    )}
                    <span className="text-xs text-gray-500">{areas.length}/{maxAreas}</span>
                  </span>
                </div>
                <div className="space-y-2">
                  {areas.map((area, idx) => {
                    const color = AREA_COLORS[idx % AREA_COLORS.length];
                    const sel = selectedIdx === idx;
                    const light = (area.theme ?? "light") === "light";
                    return (
                      <div key={area.id} onClick={() => setSelectedIdx(idx)}
                        className={`cursor-pointer rounded-lg border p-2.5 transition-all ${sel ? "border-indigo-300 bg-indigo-50/60 shadow-sm" : "border-gray-200 hover:border-gray-300"}`}>
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color.stroke }} />
                          <div className="relative min-w-0 flex-1">
                            <select value={area.label}
                              onChange={(e) => { e.stopPropagation(); updateAreaLabel(idx, e.target.value); }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full cursor-pointer appearance-none truncate bg-transparent pr-5 text-xs font-semibold text-gray-800 outline-none">
                              {!AREA_LABELS.some(l => l.id === area.label) && <option value={area.label}>{areaLabelText(area.label)}</option>}
                              {AREA_LABELS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                            </select>
                            <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400" />
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); updateAreaTheme(idx, light ? "dark" : "light"); }}
                            title={light ? "Açık renkli tasarım — koyu tişört için (değiştir: koyu)" : "Koyu renkli tasarım — açık tişört için (değiştir: açık)"}
                            className="flex h-6 items-center gap-1 rounded-md border border-gray-200 px-1.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50">
                            <span className="h-2.5 w-2.5 rounded-full border border-gray-300" style={{ backgroundColor: light ? "#ffffff" : "#1a1a1a" }} />
                            {light ? "Açık" : "Koyu"}
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); removeArea(idx); }} title="Alanı sil"
                            className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-500">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-1 rounded-lg bg-gray-100 p-0.5" onClick={(e) => e.stopPropagation()}>
                          {SHAPE_OPTIONS.map((o) => {
                            const Icon = SHAPE_ICON[o.id];
                            const on = (area.shape ?? "rect") === o.id;
                            return (
                              <button key={o.id} type="button" title={o.hint} onClick={() => updateAreaShape(idx, o.id)}
                                className={`flex items-center justify-center gap-1 rounded-md py-1 text-[11px] font-medium transition-colors ${on ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                                <Icon className="h-3 w-3" /> {o.label}
                              </button>
                            );
                          })}
                        </div>
                        {surfaces[area.id] && (
                          <div className="mt-1.5 flex items-center justify-between rounded-md bg-emerald-50 px-2 py-1 text-[10px] text-emerald-700" onClick={(e) => e.stopPropagation()}
                            title="Tasarım yalnızca bu ürünün görünen yüzeyinde basılır; öndeki ürün ya da katlanmış kol üstünde kalır">
                            <span>Kendi ürününe kırpılı</span>
                            <button type="button" className="ml-auto mr-2 font-medium hover:text-emerald-900"
                              title="Bu alanın yüzeyini mor göster; fırça, çokgen ya da tıkla-seç ile düzelt"
                              onClick={() => (surfaceEditId === area.id ? (setSurfaceEditId(null), setMaskMode(false)) : startSurfaceEdit(idx))}>
                              {surfaceEditId === area.id ? "Bitti" : "Yüzeyi düzenle"}
                            </button>
                            <button type="button" className="font-medium hover:text-emerald-900"
                              onClick={() => {
                                pendingSurfacesRef.current.delete(area.id);
                                if (surfaceEditId === area.id) { setSurfaceEditId(null); setMaskMode(false); }
                                setSurfaces((prev) => { const next = { ...prev }; delete next[area.id]; return next; });
                                setAreas((prev) => prev.map((a, i) => { if (i !== idx) return a; const next = { ...a }; delete next.surfaceUrl; return next; }));
                              }}>Kaldır</button>
                          </div>
                        )}
                        {sel && (
                          <div className="mt-2 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-medium text-gray-500">Çarpıtma</span>
                              <div className="flex rounded-md bg-gray-100 p-0.5">
                                {([["none", "Yok"], ["cylinder", "Silindir"], ["perspective", "Perspektif"]] as const).map(([k, label]) => {
                                  const on = (area.warp?.type ?? "none") === k;
                                  return (
                                    <button key={k} type="button"
                                      title={k === "cylinder" ? "Kupa, termos, cam bardak, mum kavanozu" : k === "perspective" ? "Paspas, mat, tabak — köşeleri sürükle" : "Düz baskı"}
                                      onClick={() => updateAreaWarp(idx, k === "none" ? null
                                        : k === "cylinder" ? { type: "cylinder", arc: 120, bend: 0.05 }
                                        : { type: "perspective", corners: [[0, 0], [0, 0], [0, 0], [0, 0]] })}
                                      className={`rounded px-2 py-0.5 text-[10px] font-medium ${on ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                                      {label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            {area.warp?.type === "cylinder" && (() => {
                              const cp = cylinderParams(area.warp);
                              const set = (p: Partial<typeof cp>) => updateAreaWarp(idx, { type: "cylinder", ...cp, ...p });
                              const row = (label: string, title: string, val: number, min: number, max: number, step: number, fmt: string, on: (v: number) => void) => (
                                <label className="flex items-center gap-2 text-[10px] text-gray-600" title={title}>
                                  <span className="w-14 shrink-0">{label}</span>
                                  <input type="range" min={min} max={max} step={step} value={val} onChange={(e) => on(Number(e.target.value))} className="min-w-0 flex-1" />
                                  <span className="w-9 text-right tabular-nums">{fmt}</span>
                                </label>
                              );
                              return (
                                <div className="space-y-1 rounded-md bg-gray-50 p-2">
                                  {row("Kavis", "Tasarımın silindirin ne kadarını sardığı. Alan ürünün görünen genişliği kadarsa ~150-170.", cp.arc, 0, 175, 5, `${cp.arc}°`, (v) => set({ arc: v }))}
                                  {row("Üst eğim", "Üst kenarın kavisi. Tuvalde üst ortadaki noktayı sürükleyerek de ayarlanır.", Math.round(cp.bendTop * 100), -30, 30, 1, String(Math.round(cp.bendTop * 100)), (v) => set({ bendTop: v / 100 }))}
                                  {row("Alt eğim", "Alt kenarın kavisi. Tuvalde alt ortadaki noktayı sürükleyerek de ayarlanır.", Math.round(cp.bendBottom * 100), -30, 30, 1, String(Math.round(cp.bendBottom * 100)), (v) => set({ bendBottom: v / 100 }))}
                                  {row("Daralma", "Alt genişlik / üst genişlik. Alta doğru incelen termos ve bardaklar için <%100. Tuvalde sağ alt noktayla da ayarlanır.", Math.round(cp.taper * 100), 60, 140, 1, `%${Math.round(cp.taper * 100)}`, (v) => set({ taper: v / 100 }))}
                                  <p className="pt-0.5 text-[9px] text-gray-400">Tuvaldeki turuncu noktaları sürükleyerek de ayarlayabilirsin.</p>
                                </div>
                              );
                            })()}
                            {area.warp?.type === "perspective" && (
                              <div className="flex items-center justify-between rounded-md bg-gray-50 px-2 py-1.5 text-[10px] text-gray-600">
                                <span>Tuvaldeki 4 köşe noktasını ürünün köşelerine sürükle.</span>
                                <button type="button" onClick={() => updateAreaWarp(idx, { type: "perspective", corners: [[0, 0], [0, 0], [0, 0], [0, 0]] })}
                                  className="ml-2 shrink-0 rounded border border-gray-200 bg-white px-1.5 py-0.5 hover:bg-gray-100">Sıfırla</button>
                              </div>
                            )}
                          </div>
                        )}
                        {sel && (
                          <div className="mt-2 flex items-center justify-between text-[10px] tabular-nums text-gray-500">
                            <span>{Math.round(area.w)}×{Math.round(area.h)} px{!isFreeAspect(area) && (isPrintRatio(area) ? " · 7:8" : <span className="text-amber-600"> · oran 7:8 değil</span>)}</span>
                            <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <button type="button" onClick={() => nudgeRotation(-0.5)} title="Sola döndür (A)"
                                className="rounded border border-gray-200 bg-white p-0.5 hover:bg-gray-50"><RotateCcw className="h-3 w-3" /></button>
                              <span className="w-10 text-center">{(area.rotation || 0).toFixed(1)}°</span>
                              <button type="button" onClick={() => nudgeRotation(0.5)} title="Sağa döndür (D)"
                                className="rounded border border-gray-200 bg-white p-0.5 hover:bg-gray-50"><RotateCw className="h-3 w-3" /></button>
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {areas.length < maxAreas && (
                  <button onClick={addArea}
                    className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 py-2 text-xs font-medium text-gray-600 hover:border-indigo-300 hover:bg-indigo-50/40 hover:text-indigo-700">
                    <Plus className="h-3.5 w-3.5" /> Alan ekle
                  </button>
                )}
                <p className="mt-1.5 text-[10px] text-gray-400">Ya da tuvalde boş bir yerde sürükleyerek çiz. Dikdörtgen alanlar baskı alanı oranında (7:8 dikey, tişört 4200×4800).</p>
                {areas.some((a) => !isFreeAspect(a) && !isPrintRatio(a)) && (
                  <button type="button" onClick={() => setAreas((prev) => prev.map((a) => (!isFreeAspect(a) && !isPrintRatio(a) ? toPrintRatio(a) : a)))}
                    className="mt-2 w-full rounded-lg border border-amber-300 bg-amber-50 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                    title="Yükseklik ve merkez korunur, genişlik 7:8'e daralır">
                    {areas.filter((a) => !isFreeAspect(a) && !isPrintRatio(a)).length} alanı 7:8 baskı oranına çevir
                  </button>
                )}
              </section>

              {/* 3. Maske */}
              <section ref={maskSectionRef}>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className={sectionTitle}>Maske katmanı</h3>
                  <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${hasMask ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                    {hasMask ? <><Check className="h-3 w-3" /> {surfaceMode ? "Yüzey maskesi" : "Ön plan boyası"}</> : "Yok"}
                  </span>
                </div>
                <p className="mb-2 text-[11px] leading-relaxed text-gray-500">
                  {surfaceMode ? "Mor" : "Kırmızı"} yerler tasarımın önünde kalır: delik, ip, kenar, arka plan.
                </p>
                {surfaceEditId && (() => {
                  const ei = areas.findIndex((a) => a.id === surfaceEditId);
                  return (
                    <div className="mb-2 flex items-center justify-between rounded-lg bg-violet-50 px-2.5 py-2 text-[11px] text-violet-800 ring-1 ring-violet-200">
                      <span><b>{ei >= 0 ? areaLabelText(areas[ei].label) : "Alan"}</b> yüzeyi düzenleniyor — mor yerlerde bu alanın tasarımı görünmez. Tıkla-seç + Gizle: öndeki kolu tek tıkla çıkar.</span>
                      <button type="button" onClick={() => { setSurfaceEditId(null); setMaskMode(false); }}
                        className="ml-2 shrink-0 rounded-md bg-violet-600 px-2 py-1 font-medium text-white hover:bg-violet-700">Bitti</button>
                    </div>
                  );
                })()}
                <button onClick={() => { setMaskMode((v) => !v); setAiClick(false); setSurfaceEditId(null); }} disabled={areas.length === 0}
                  className={`flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50 ${
                    maskMode ? "bg-amber-500 text-white hover:bg-amber-600" : "border border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
                  <Layers className="h-3.5 w-3.5" /> {maskMode ? "Maske düzenlemeyi bitir" : "Maskeyi düzenle"}
                </button>

                {maskMode && (
                  <div className="mt-2 space-y-2.5 rounded-lg border border-amber-200 bg-amber-50/50 p-2.5">
                    <div className="grid grid-cols-2 gap-1.5">
                      <button onClick={autoSurface} disabled={aiBusy || !!surfaceEditId}
                        title="Mevcut alanların merkezinden ürün yüzeyini yeniden bul"
                        className="flex items-center justify-center gap-1 rounded-md bg-violet-600 px-2 py-1.5 text-[11px] font-medium text-white hover:bg-violet-700 disabled:opacity-50">
                        <Sparkles className="h-3 w-3" /> AI yüzey
                      </button>
                      <button onClick={() => setAiClick((v) => !v)}
                        title="Açıkken tıkladığın yerdeki yüzey eklenir"
                        className={`flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium ${aiClick ? "bg-violet-600 text-white" : "border border-violet-200 bg-white text-violet-700"}`}>
                        <MousePointerClick className="h-3 w-3" /> Tıkla-seç
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-1 rounded-md bg-white p-0.5 ring-1 ring-amber-200">
                      <button onClick={() => { setMaskTool("brush"); setAiClick(false); setPolyPts([]); }}
                        className={`flex items-center justify-center gap-1 rounded py-1 text-[11px] font-medium ${maskTool === "brush" && !aiClick ? "bg-gray-900 text-white" : "text-gray-700"}`}>
                        <Brush className="h-3 w-3" /> Fırça
                      </button>
                      <button onClick={() => { setMaskTool("polygon"); setAiClick(false); }}
                        title="Noktaları tıklayarak alan seç; ilk noktaya tıkla, çift tıkla ya da Enter ile kapat"
                        className={`flex items-center justify-center gap-1 rounded py-1 text-[11px] font-medium ${maskTool === "polygon" && !aiClick ? "bg-gray-900 text-white" : "text-gray-700"}`}>
                        <PenTool className="h-3 w-3" /> Çokgen
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-1 rounded-md bg-white p-0.5 ring-1 ring-amber-200">
                      <button onClick={() => { setMaskErase(false); setAiClick(false); }}
                        className={`flex items-center justify-center gap-1 rounded py-1 text-[11px] font-medium ${!maskErase && !aiClick ? "bg-amber-500 text-white" : "text-amber-800"}`}>
                        <Brush className="h-3 w-3" /> Gizle
                      </button>
                      <button onClick={() => { setMaskErase(true); setAiClick(false); }}
                        className={`flex items-center justify-center gap-1 rounded py-1 text-[11px] font-medium ${maskErase && !aiClick ? "bg-amber-500 text-white" : "text-amber-800"}`}>
                        <Eraser className="h-3 w-3" /> Göster
                      </button>
                    </div>
                    {maskTool === "brush" ? (
                      <label className="flex items-center gap-2 text-[11px] text-amber-900">
                        Boyut
                        <input type="range" min={5} max={Math.round(mockup.imageWidth / 4)} value={brushSize}
                          onChange={(e) => setBrushSize(Number(e.target.value))} className="flex-1" />
                        <span className="w-12 text-right tabular-nums">{brushSize}px</span>
                      </label>
                    ) : (
                      <div className="space-y-1.5 rounded-md bg-white px-2 py-1.5 text-[10px] leading-relaxed text-amber-900 ring-1 ring-amber-200">
                        <p>Tuvalde noktaları tıkla. <b>İlk noktaya</b> tıkla, <b>çift tıkla</b> ya da <b>Enter</b>: alan {maskErase ? "GÖSTERİLİR" : "GİZLENİR"}. Esc iptal, Backspace son noktayı siler.</p>
                        {polyPts.length > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="mr-auto tabular-nums">{polyPts.length} nokta</span>
                            <button onClick={() => setPolyPts((p) => p.slice(0, -1))} className="flex items-center gap-0.5 rounded border border-amber-200 px-1.5 py-0.5 hover:bg-amber-50"><Undo2 className="h-3 w-3" /> Geri</button>
                            <button onClick={() => closePolygon()} disabled={polyPts.length < 3}
                              className="flex items-center gap-0.5 rounded bg-amber-500 px-1.5 py-0.5 text-white hover:bg-amber-600 disabled:opacity-40"><Check className="h-3 w-3" /> Kapat</button>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setPreview((v) => !v)}
                        className={`flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1 text-[11px] ${preview ? "bg-gray-900 text-white" : "border border-amber-200 bg-white text-amber-800"}`}>
                        <Eye className="h-3 w-3" /> Önizleme
                      </button>
                      <button onClick={clearMask} disabled={!!surfaceEditId}
                        className="flex flex-1 items-center justify-center gap-1 rounded-md border border-red-200 bg-white px-2 py-1 text-[11px] text-red-600 hover:bg-red-50">
                        <Trash2 className="h-3 w-3" /> Temizle
                      </button>
                    </div>
                    {!surfaceMode && (
                      <details className="text-[11px] text-amber-900">
                        <summary className="cursor-pointer select-none font-medium">Gelişmiş: parlak yerler (tişört)</summary>
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <button onClick={markBrightAreas}
                            title="Alan içindeki parlak pikselleri (kumaş kıvrımı, omuz parlaması) ön plana al"
                            className="flex items-center gap-1 rounded bg-amber-500 px-2 py-1 text-white hover:bg-amber-600">
                            <Sun className="h-3 w-3" /> Isaretle
                          </button>
                          <input type="range" min={2} max={40} value={brightPercent}
                            onChange={(e) => setBrightPercent(Number(e.target.value))} className="flex-1" />
                          <span className="w-9 text-right tabular-nums">%{brightPercent}</span>
                        </div>
                      </details>
                    )}
                    {maskCoverage >= 85 && (
                      <p className="rounded bg-red-100 px-2 py-1 text-[11px] text-red-700">Alanın %{maskCoverage}'i kapalı — tasarım neredeyse görünmez.</p>
                    )}
                  </div>
                )}
              </section>

              {/* 4. Isik */}
              <section>
                <div className="mb-1.5 flex items-center justify-between">
                  <h3 className={sectionTitle}>Işık ve gölge</h3>
                  <span className="text-[11px] tabular-nums text-gray-600">{shading === 0 ? "Kapalı" : `%${shading}`}</span>
                </div>
                <input type="range" min={0} max={100} step={5} value={shading}
                  onChange={(e) => setShading(Number(e.target.value))} className="w-full accent-amber-500" />
                <p className="mt-1 text-[11px] leading-relaxed text-gray-500">
                  Ürünün parlaması ve gölgesi tasarımın üstüne gelir; baskı gerçek gibi durur. Seramik/kupa için %60-90. Sonuç Studio'daki görselde görünür.
                </p>
                <div className="mb-1.5 mt-3 flex items-center justify-between">
                  <h3 className={sectionTitle}>Kumaş kırışığı</h3>
                  <span className="text-[11px] tabular-nums text-gray-600">{displace === 0 ? "Kapalı" : `%${displace}`}</span>
                </div>
                <input type="range" min={0} max={100} step={5} value={displace}
                  onChange={(e) => setDisplace(Number(e.target.value))} className="w-full accent-amber-500" />
                <p className="mt-1 text-[11px] leading-relaxed text-gray-500">
                  Tasarım fotoğraftaki kıvrımlar boyunca hafifçe eğilir. Tişört/hoodie/çanta için %40-70; düz seramik ve camda kapalı bırak.
                  Işık ya da kırışık açıksa baskıya fotoğrafın kendi greni de eklenir.
                </p>
              </section>

              {/* 5. Renk etiketi */}
              <section>
                <div className="mb-1.5 flex items-center justify-between">
                  <h3 className={sectionTitle}>Renk etiketi</h3>
                  <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600">
                    <input type="checkbox" className="accent-indigo-600" checked={!!colorLabel?.enabled}
                      onChange={(e) => { patchLabel({ enabled: e.target.checked }); if (e.target.checked) setLabelSelected(true); }} />
                    Bas
                  </label>
                </div>
                <label className="mb-1 block text-xs font-medium text-gray-700" htmlFor="mockup-color-name">Renk adı</label>
                <input id="mockup-color-name" value={colorName} onChange={(e) => setColorName(e.target.value)} placeholder="ör. Military Green"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                <p className="mt-1 text-xs text-gray-500">Listedeki renk seçeneğiyle birebir aynı yaz; alıcı fotoğraftaki rengi menüde bulsun.</p>
                {colorLabel?.enabled && (
                  <div className="mt-3 space-y-3">
                    {!colorName.trim() && <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-700">Renk adı boş — etiket basılmaz.</p>}
                    <p className="text-xs text-gray-500">Görselde sürükleyerek taşı, köşe tutamacıyla büyüt/küçült.</p>
                    <div>
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-700">Boyut</span>
                        <span className="text-xs tabular-nums text-gray-500">%{Math.round(colorLabel.size * 1000) / 10}</span>
                      </div>
                      <input type="range" min={0.02} max={0.2} step={0.005} value={colorLabel.size}
                        onChange={(e) => patchLabel({ size: Number(e.target.value) })} className="w-full accent-indigo-600" aria-label="Etiket boyutu" />
                    </div>
                    <div>
                      <span className="mb-1 block text-xs font-medium text-gray-700">Konum</span>
                      <div className="grid grid-cols-4 gap-1">
                        {([["Sol üst", 0, 0], ["Sağ üst", 1, 0], ["Sol alt", 0, 1], ["Sağ alt", 1, 1]] as const).map(([t, rx, ry]) => (
                          <button key={t} type="button"
                            onClick={() => {
                              const m = 0.03, bw = (labelBox?.w ?? 0) / mockup.imageWidth, bh = (labelBox?.h ?? 0) / mockup.imageHeight;
                              patchLabel({ x: rx ? 1 - m - bw : m, y: ry ? 1 - m * (mockup.imageWidth / mockup.imageHeight) - bh : m * (mockup.imageWidth / mockup.imageHeight) });
                            }}
                            className="rounded-lg border border-gray-200 px-1 py-1.5 text-xs text-gray-700 hover:bg-gray-50">{t}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="mb-1 block text-xs font-medium text-gray-700">Biçim</span>
                      <div className="grid grid-cols-3 gap-1">
                        {([["pill", "Yuvarlak"], ["box", "Köşeli"], ["brush", "Fırça"], ["none", "Zeminsiz"]] as const).map(([v, t]) => (
                          <button key={v} type="button" onClick={() => patchLabel({ shape: v })}
                            className={`rounded-lg border px-1 py-1.5 text-xs ${colorLabel.shape === v ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}>{t}</button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center gap-2 text-xs text-gray-700">
                        <input type="color" value={colorLabel.textColor} onChange={(e) => patchLabel({ textColor: e.target.value })}
                          className="h-7 w-9 cursor-pointer rounded border border-gray-200" aria-label="Yazı rengi" />
                        Yazı
                      </label>
                      {colorLabel.shape !== "none" && (
                        <label className="flex items-center gap-2 text-xs text-gray-700">
                          <input type="color" value={colorLabel.bg} onChange={(e) => patchLabel({ bg: e.target.value })}
                            className="h-7 w-9 cursor-pointer rounded border border-gray-200" aria-label="Zemin rengi" />
                          Zemin
                        </label>
                      )}
                    </div>
                    {colorLabel.shape !== "none" && (
                      <button type="button" onClick={() => setPickLabelBg((v) => !v)} aria-pressed={pickLabelBg}
                        className={`w-full rounded-lg border px-3 py-1.5 text-xs font-medium ${pickLabelBg ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
                        {pickLabelBg ? "Görselde tişörte tıkla…" : "Zemini tişörtten seç"}
                      </button>
                    )}
                    {colorLabel.shape !== "none" && (
                      <div>
                        <div className="mb-1 flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-700">Zemin opaklığı</span>
                          <span className="text-xs tabular-nums text-gray-500">%{Math.round(colorLabel.bgOpacity * 100)}</span>
                        </div>
                        <input type="range" min={0} max={1} step={0.05} value={colorLabel.bgOpacity}
                          onChange={(e) => patchLabel({ bgOpacity: Number(e.target.value) })} className="w-full accent-indigo-600" aria-label="Zemin opaklığı" />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => patchLabel({ bg: "#ffffff", textColor: "#222222", bgOpacity: 0.88 })}
                        className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">Açık</button>
                      <button type="button" onClick={() => patchLabel({ bg: "#222222", textColor: "#ffffff", bgOpacity: 0.8 })}
                        className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">Koyu</button>
                      <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-xs text-gray-700">
                        <input type="checkbox" className="accent-indigo-600" checked={colorLabel.uppercase}
                          onChange={(e) => patchLabel({ uppercase: e.target.checked })} />
                        BÜYÜK HARF
                      </label>
                    </div>
                  </div>
                )}
              </section>
            </div>

            {/* Alt cubuk */}
            <div className="flex items-center gap-2 border-t border-gray-100 bg-white p-3">
              <button onClick={onCancel} className="rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100">{backLabel ?? "İptal"}</button>
              {onSaveAndApplyAll && (
                <button
                  onClick={async () => { setSaving(true); await persistMask(); const saved = await areasWithUploadedSurfaces(areas); setAreas(saved); await onSaveAndApplyAll(saved); setSaving(false); }}
                  disabled={areas.length === 0 || saving}
                  title="Bu alanları aynı boyuttaki tüm mockuplara uygula"
                  className="flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
                  <Copy className="h-3.5 w-3.5" /> Hepsine
                </button>
              )}
              <button onClick={handleSave} disabled={areas.length === 0 || saving}
                className="ml-auto flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Kaydet
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
